import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

type Lang = "en" | "it";

const translations: Record<Lang, Record<string, string>> = {
  en: {
    "header.subtitle": "High‑performance e‑commerce solutions",
    "header.go_home_aria": "Go to homepage",
    "header.homepage_title": "Homepage",
    "country.en": "England",
    "country.it": "Italy",
    "home.h1":
      "BuildStore: E-commerce Development | Online Store. Turnkey Solution.",
    "home.p":
      "Launch your 100% owned online store starting from €699. Zero subscriptions, zero commissions: your definitive platform, yours forever. High-performance infrastructure optimized for sales. Stop paying rent for your business, own your future.",
    "home.view_products": "Explore Templates",
    "home.contact_sales": "Contact sales",
    "home.book_deploy": "Choose Your Package",
    "home.book_deploy_sub": "15-Day Delivery • Yours Forever",
    "feature.performance.title": "Super Fast",
    "feature.performance.desc":
      "Maximized Loading Speed. Fewer Bounces, Better Google Ranking.",
    "feature.security.title": "Safe & Protected",
    "feature.security.desc":
      "Guaranteed Security and Reliability. We handle protected transactions using SSL/TLS encryption and automatic backups. Your data and customer data are monitored 24/7.",
    "feature.integrations.title": "Integrations for a Complete E-commerce.",
    "feature.integrations.desc":
      "Accept easy and secure payments (cards, PayPal, Stripe), manage shipping with major carriers, and sync your inventory.",
    "packages.title": "Choose Your E-commerce and Let's Start",
    "packages.subtitle":
      "Every example is an online store we will customize for your brand.",
    "packages.open": "See the Preview",
    "packages.building": "Coming Soon...",
    "packages.long_description":
      "We fully customize it and launch it in 15 days. Professional solutions starting from €699.",
    "packages.card_description":
      "Complete e‑commerce template for sports retail.",
    "home.portfolio_label": "Real examples from our work",
    "home.portfolio_projects":
      "Real working examples we adapt for your brand. You see exactly how your store will look before we even start.",
    "home.portfolio_details":
      "Every example includes everything: domain, secure payments, and SEO. Everything included in your chosen package. Explore options in the Pricing section.",
    "contact.title": "Let's Build Your E-commerce",
    "contact.p":
      "We offer a range of custom e-commerce solutions tailored to your business needs. Whether you're looking for a complete turnkey package or specific services, let's discuss what's right for you.",
    "contact.contact_sales": "Contact us",
    "contact.admin": "Admin",
    "contact.pricing_link": "View Our Pricing",

    "contact.form.name": "Full Name",
    "contact.form.name_placeholder": "Your name",
    "contact.form.email": "Email Address",
    "contact.form.email_placeholder": "your@email.com",
    "contact.form.phone": "Phone Number",
    "contact.form.phone_placeholder": "+39 ...",
    "contact.form.optional": "optional",
    "contact.form.message": "Message",
    "contact.form.message_placeholder": "Tell us about your project...",
    "contact.form.submit": "Send Message",
    "contact.form.sending": "Sending...",
    "contact.form.success.title": "Message Sent",
    "contact.form.success.desc": "Thank you! We'll be in touch shortly.",
    "contact.form.error.title": "Error",
    "contact.form.error.send_failed":
      "Failed to send message. Please try again.",
    "contact.form.error.required": "Required Fields",
    "contact.form.error.fill_fields": "Please fill in all required fields.",
    "contact.alternative_text": "Prefer to chat?",
    "contact.whatsapp_cta": "Contact via WhatsApp",
    "contact.form.label": "Get in Touch",
    "contact.form.description":
      "Fill out the form below and we'll get back to you within 24 hours.",
    "footer.contact_button": "Contact Us",

    "home.why_buildstore_title": "Why BuildStore vs. Shopify and Webador?",
    "home.why_buildstore_desc":
      "Discover how our modern infrastructure gives you the speed, ownership, and scalability that standard builders cannot provide.",
    "home.read_comparison": "Read the Full Comparison",

    "about.hero.title": "About BuildStore — A Different Approach to E-commerce",
    "about.hero.subtitle":
      "We believe every business deserves a fast, reliable, and truly owned online store. That's why we built BuildStore.",
    "about.mission.title": "Our Mission",
    "about.mission.desc":
      "Liberate online stores from monthly subscriptions and hidden commissions. We give you full ownership of your platform, absolute transparency in pricing, and the speed that Google rewards.",
    "about.mission.quote":
      "Your store is yours, forever. No subscriptions, no commissions, no limits.",
    "about.why.title": "Why We Do This",
    "about.why.desc":
      "Too many business owners are trapped in SaaS platforms that take a cut of every sale while limiting their growth. We wanted to create an alternative: a modern, fast, and truly owned solution that lets you focus on selling, not on paying ongoing fees.",
    "about.why.quote":
      "Build once, own forever. That's the BuildStore promise.",
    "about.tech.title": "Built on Modern Technology",
    "about.tech.desc":
      "We use the latest infrastructure to ensure your store is always fast, secure, and scalable.",
    "about.values.title": "Our Core Values",
    "about.values.speed_title": "Speed",
    "about.values.speed_desc":
      "Every millisecond matters. We optimize for Core Web Vitals and Google ranking.",
    "about.values.ownership_title": "Ownership",
    "about.values.ownership_desc":
      "100% of your code and data is yours. No vendor lock-in, no surprises.",
    "about.values.scalability_title": "Scalability",
    "about.values.scalability_desc":
      "Built to grow with you, from your first sale to millions of customers.",
    "about.cta.title": "Ready to Own Your Platform?",
    "about.cta.desc":
      "Join hundreds of businesses that have chosen BuildStore for their online presence.",
    "about.cta.button": "Start Your Project",

    "index.loading": "Loading products...",
    "index.error_title": "Error loading products",
    "index.btn_retry": "Retry",
    "index.btn_test_server": "Test server",
    "index.btn_use_fallback": "Use alternative server",
    "index.suggestion":
      "Open DevTools → Network to inspect /api/products. If you see 404/502 or CORS, the backend is unreachable from this origin.",
    "index.hero.title":
      "Our Portfolio: Real Examples of Winning E-commerce Stores",
    "index.hero.desc":
      "Explore the high-performance online stores we have developed for fashion, food, cosmetics, and other industries. Each project is a functional, customizable e-commerce solution for your brand, ready to generate sales. Choose the package that fits your needs, with professional solutions starting from €699.",

    "footer.email": "Email",
    "footer.phone": "Phone",
    "footer.phone_hidden": "+39 350 593 3542",
    "footer.address": "Address",
    "footer.links.home": "Home",
    "footer.links.products": "Projects",
    "footer.links.valore": "Value",
    "footer.links.about": "About",
    "footer.links.contact": "Contact",
    "footer.links.faq": "FAQ",
    "footer.links.admin": "Admin",
    "nav.projects": "Projects",
    "nav.pricing": "Pricing",
    "nav.blog": "Blog",
    "nav.value": "Value",
    "nav.about": "About Us",
    "nav.case_studies": "Case Studies",
    "nav.contact": "Contact",

    "pricing.hero.title": "Transparent and Competitive Pricing",
    "pricing.hero.description":
      "Professional e-commerce solutions at honest prices. No hidden subscriptions, no commissions. All yours forever.",
    "pricing.main_packages": "Main Packages",
    "pricing.additional_services": "Additional Services",
    "pricing.faq_title": "Frequently Asked Questions",
    "pricing.most_popular": "Most Popular",
    "pricing.cta_title": "Ready to Get Started?",
    "pricing.cta_description":
      "Contact us for a free consultation and discover the perfect package for you.",
    "pricing.cta_button": "Book a Consultation",
    "pricing.cta_whatsapp": "Chat on WhatsApp",

    "pricing.package.showcase.name": "Showcase Site",
    "pricing.package.showcase.description": "Showcase your business online",
    "pricing.package.showcase.cta": "Request quote",

    "pricing.package.spa.name": "E-commerce SPA",
    "pricing.package.spa.description": "Single product with cart",
    "pricing.package.spa.cta": "Start now",

    "pricing.package.20.name": "E-commerce 20 products",
    "pricing.package.20.description": "Complete small catalog",
    "pricing.package.20.cta": "Select",

    "pricing.package.50.name": "E-commerce 50 products",
    "pricing.package.50.description": "Complete medium catalog",
    "pricing.package.50.cta": "Select",

    "pricing.package.200.name": "E-commerce 50-200 products",
    "pricing.package.200.description":
      "Extended catalog with advanced features",
    "pricing.package.200.cta": "Request",

    "pricing.package.enterprise.name": "E-commerce 200+ products",
    "pricing.package.enterprise.description": "Custom enterprise solutions",
    "pricing.package.enterprise.price": "Upon request",
    "pricing.package.enterprise.cta": "Contact",

    "pricing.service.hosting.name": "Hosting + Domain",
    "pricing.service.hosting.description":
      "Managed hosting on scalable infrastructure",
    "pricing.service.hosting.cta": "Add",

    "pricing.service.seo.name": "SEO Base",
    "pricing.service.seo.description": "Initial search engine optimization",
    "pricing.service.seo.cta": "Add",

    "pricing.service.maintenance.name": "Monthly Maintenance",
    "pricing.service.maintenance.description":
      "Continuous support and bug fixes",
    "pricing.service.maintenance.cta": "Subscribe",

    "pricing.service.migration.name": "Platform Migration",
    "pricing.service.migration.description": "Migration from other platforms",
    "pricing.service.migration.cta": "Quote",

    "pricing.service.bugfix.name": "Urgent Bug Fixes",
    "pricing.service.bugfix.description": "Quick problem resolution",
    "pricing.service.bugfix.cta": "Contact",

    "pricing.service.multilingual.name": "Multilingual Support",
    "pricing.service.multilingual.description":
      "Internationalize your store with integrated translations",
    "pricing.service.multilingual.cta": "Add",

    "pricing.faq.q1": "Can I upgrade my plan in the future?",
    "pricing.faq.a1":
      "Yes, you can always upgrade your e-commerce by adding more products or features. We will discuss the migration cost with you.",
    "pricing.faq.q2": "Are security updates included?",
    "pricing.faq.a2":
      "Yes, security updates are always included. If you subscribe to monthly maintenance, it also manages dependencies.",
    "pricing.faq.q3": "What happens after 30 days of post-launch support?",
    "pricing.faq.a3":
      "You still have full access to your store. You can subscribe to monthly maintenance (79€) for ongoing support, or contact us for specific interventions (50€/hour).",
    "pricing.faq.q4": "Can I transfer my site in the future?",
    "pricing.faq.a4":
      "Yes, the code is completely yours. You can move it to any server or provider that supports Node.js + Supabase.",
    "pricing.faq.q5": "What payment methods do you accept?",
    "pricing.faq.a5":
      "We accept Stripe, bank transfers, and PayPal. Contact us to discuss payment terms.",
    "pricing.faq.q6": "Do you offer discounts for long-term projects?",
    "pricing.faq.a6":
      "Yes, contact us for annual or multi-year projects. We can discuss volume discounts and custom plans.",

    // Pricing package features - Showcase Site
    "pricing.package.showcase.features.0": "Modern responsive site",
    "pricing.package.showcase.features.1": "5-10 static pages",
    "pricing.package.showcase.features.2": "Domain included (first year)",
    "pricing.package.showcase.features.3": "SSL certificate",
    "pricing.package.showcase.features.4": "Basic contacts",
    "pricing.package.showcase.features.5": "On-page SEO",
    "pricing.package.showcase.features.6": "Optimized loading speed",

    // Pricing package features - E-commerce SPA
    "pricing.package.spa.features.0": "Complete e-commerce site",
    "pricing.package.spa.features.1": "1 product with variants",
    "pricing.package.spa.features.2": "Shopping cart & checkout",
    "pricing.package.spa.features.3": "Stripe integration",
    "pricing.package.spa.features.4": "Email notifications",
    "pricing.package.spa.features.5": "Basic admin dashboard",
    "pricing.package.spa.features.6": "Secure database",
    "pricing.package.spa.features.7": "Domain included (first year)",

    // Pricing package features - E-commerce 20
    "pricing.package.20.features.0": "Catalog up to 20 products",
    "pricing.package.20.features.1": "Advanced filters & search",
    "pricing.package.20.features.2": "Shopping cart & checkout",
    "pricing.package.20.features.3": "Stripe integration",
    "pricing.package.20.features.4": "Order management system",
    "pricing.package.20.features.5": "Complete admin dashboard",
    "pricing.package.20.features.6": "Automatic email notifications",
    "pricing.package.20.features.7": "Scalable database",
    "pricing.package.20.features.8": "Optimized performance (React)",
    "pricing.package.20.features.9": "Domain included (first year)",
    "pricing.package.20.features.10": "30 days post-launch support",

    // Pricing package features - E-commerce 50
    "pricing.package.50.features.0": "Catalog up to 50 products",
    "pricing.package.50.features.1": "Advanced filters & search",
    "pricing.package.50.features.2": "Shopping cart & checkout",
    "pricing.package.50.features.3": "Stripe integration",
    "pricing.package.50.features.4": "Order management system",
    "pricing.package.50.features.5": "Complete admin dashboard",
    "pricing.package.50.features.6": "Automatic email notifications",
    "pricing.package.50.features.7": "Scalable database",
    "pricing.package.50.features.8": "Optimized performance (React)",
    "pricing.package.50.features.9": "Domain included (first year)",
    "pricing.package.50.features.10": "30 days post-launch support",

    // Pricing package features - E-commerce 200
    "pricing.package.200.features.0": "Catalog 50-200 products",
    "pricing.package.200.features.1": "Advanced inventory management",
    "pricing.package.200.features.2": "Multi-parameter filters",
    "pricing.package.200.features.3": "Complete variant system",
    "pricing.package.200.features.4": "Optimized checkout",
    "pricing.package.200.features.5": "Stripe payment intents",
    "pricing.package.200.features.6": "Basic analytics",
    "pricing.package.200.features.7": "Advanced admin dashboard",
    "pricing.package.200.features.8": "Integrated SMTP support",
    "pricing.package.200.features.9": "Optimized database storage",
    "pricing.package.200.features.10": "Optimal performance & SEO",
    "pricing.package.200.features.11": "Domain included (first year)",
    "pricing.package.200.features.12": "30 days post-launch support",

    // Pricing package features - E-commerce Enterprise
    "pricing.package.enterprise.features.0":
      "Unlimited catalog (200+ products)",
    "pricing.package.enterprise.features.1": "Complete inventory management",
    "pricing.package.enterprise.features.2":
      "Advanced filters & full-text search",
    "pricing.package.enterprise.features.3": "Nested category system",
    "pricing.package.enterprise.features.4": "Unlimited product variants",
    "pricing.package.enterprise.features.5":
      "Advanced checkout with custom logic",
    "pricing.package.enterprise.features.6": "Custom integrations on request",
    "pricing.package.enterprise.features.7": "Enterprise admin dashboard",
    "pricing.package.enterprise.features.8": "Detailed analytics & reporting",
    "pricing.package.enterprise.features.9": "Performance monitoring",
    "pricing.package.enterprise.features.10": "Priority technical support",
    "pricing.package.enterprise.features.11": "Domain included (first year)",
    "pricing.package.enterprise.features.12": "Extended post-launch support",

    // Pricing service features - Hosting
    "pricing.service.hosting.features.0": "Enterprise hosting included",
    "pricing.service.hosting.features.1": "Database management included",
    "pricing.service.hosting.features.2": "Managed SSL & certificates",
    "pricing.service.hosting.features.3": "Global CDN",
    "pricing.service.hosting.features.4": "99.9% uptime",
    "pricing.service.hosting.features.5": "Automatic backups",
    "pricing.service.hosting.features.6": "Technical support included",

    // Pricing service features - SEO
    "pricing.service.seo.features.0": "Complete SEO audit",
    "pricing.service.seo.features.1": "Meta tags optimization",
    "pricing.service.seo.features.2": "Sitemap & robots.txt",
    "pricing.service.seo.features.3": "Schema markup implementation",
    "pricing.service.seo.features.4": "Performance optimization",
    "pricing.service.seo.features.5": "Google Analytics setup",
    "pricing.service.seo.features.6": "Basic keyword consulting",

    // Pricing service features - Maintenance
    "pricing.service.maintenance.features.0": "Bug fixes & corrections",
    "pricing.service.maintenance.features.1": "Minor updates",
    "pricing.service.maintenance.features.2": "Security monitoring",
    "pricing.service.maintenance.features.3": "Backup & recovery",
    "pricing.service.maintenance.features.4": "Priority technical support",
    "pricing.service.maintenance.features.5": "Dependency updates",
    "pricing.service.maintenance.features.6": "Performance tuning",

    // Pricing service features - Migration
    "pricing.service.migration.features.0": "Current structure analysis",
    "pricing.service.migration.features.1": "Data & product import",
    "pricing.service.migration.features.2": "URL redirect management",
    "pricing.service.migration.features.3": "Complete testing & QA",
    "pricing.service.migration.features.4": "Support during transition",
    "pricing.service.migration.features.5": "Team training",
    "pricing.service.migration.features.6": "Custom complexity assessment",

    // Pricing service features - Bug Fixes
    "pricing.service.bugfix.features.0": "Fast response (< 2 hours)",
    "pricing.service.bugfix.features.1": "Priority resolution",
    "pricing.service.bugfix.features.2": "Documentation included",
    "pricing.service.bugfix.features.3": "Testing & deploy",
    "pricing.service.bugfix.features.4": "Precise per-minute billing",
    "pricing.service.bugfix.features.5": "Phone support available",

    // Pricing service features - Multilingual Support
    "pricing.service.multilingual.features.0":
      "Multilingual URL structure implementation",
    "pricing.service.multilingual.features.1":
      "Content management in Italian + other languages",
    "pricing.service.multilingual.features.2":
      "Interface and static text translation",
    "pricing.service.multilingual.features.3":
      "Interface for managing product translations",
    "pricing.service.multilingual.features.4":
      "Multilingual SEO optimization (Hreflang tags)",
    "pricing.service.multilingual.features.5":
      "Supported languages: English, French, Spanish, German, Portuguese",

    "footer.description":
      "Custom e‑commerce solutions that are scalable and secure — fast implementation, reliable integrations, modern hosting and secure payments via Stripe.",
    "home.p_short":
      "Tailored e‑commerce solutions: custom online stores, admin panels and payment integrations for direct sales.",
    "footer.contact_form": "Contact Form",
    "footer.quick_links": "Quick Links",
    "footer.copyright": "All rights reserved.",

    "auth.login": "Login",
    "auth.logout": "Logout",

    placeholder: "Placeholder",

    "product.preview": "Preview",
    "product.preview_note": "Preview — click to open",
    "product.case_study_label": "Real working example",
    "product.case_study_tagline": "Your online store, ready to sell",
    "product.building": "Building...",
    "product.building_note": "Building... — not addable",
    "product.add_to_cart": "Add to cart",
    "product.view_details": "View details",

    "cart.open": "Open cart",
    "cart.close": "Close",
    "cart.empty": "No items in your cart.",
    "cart.remove": "Remove",
    "cart.total": "Total",
    "cart.clear": "Clear",
    "cart.checkout": "Checkout",
    "cart.shipping_title": "Shipping address",
    "cart.placeholder.name": "Full name",
    "cart.placeholder.address": "Street, number",
    "cart.placeholder.city": "City",
    "cart.placeholder.postal": "Postal code",
    "cart.placeholder.country": "Country",
    "cart.error.login_required": "You must be logged in to proceed to checkout",
    "cart.error.fill_shipping": "Fill all shipping fields",
    "cart.error.checkout_failed": "Error during checkout",
    "cart.processing": "Processing...",
    "cart.proceed": "Proceed to payment",

    "login.title.login": "Login",
    "login.title.register": "Register",
    "login.title.reset": "Reset password",
    "login.desc.login": "Enter email and password to continue.",
    "login.desc.register":
      "Create an account by entering your contact details.",
    "login.desc.reset":
      "Reset your password by entering your email. It will be temporarily set to '123456789'.",
    "login.placeholder.email": "Email",
    "login.placeholder.password": "Password",
    "login.placeholder.name": "Full name",
    "login.placeholder.phone": "Phone number",
    "login.placeholder.address": "Street, number",
    "login.placeholder.city": "City",
    "login.placeholder.postal": "Postal code",
    "login.placeholder.country": "Country",
    "login.btn.cancel": "Cancel",
    "login.links.no_account": "Don't have an account? Register",
    "login.links.forgot": "Forgot password?",
    "login.links.have_account": "Already have an account? Login",
    "login.links.back_to_login": "Back to login",
    "login.info.reset":
      "Password reset to '123456789'. Use this password to sign in.",
    "login.error.fill_register": "Fill name, phone and address",
    "login.error.login_failed": "Error during login",
    "login.error.register_failed": "Error during registration",
    "login.error.reset_failed": "Error during reset",

    "auth.error.user_not_found": "User not found. Register first.",
    "auth.error.wrong_password": "Incorrect password",
    "auth.error.email_required": "Email and password required",
    "auth.error.account_exists": "Account already exists for this email",

    // product specific translations (english)
    "product.sport-gear-store-basic.name": "Sport Gear Store — Basic",
    "product.sport-gear-store-basic.description":
      "Base store ready to sell with catalog, template and checkout; ideal to start quickly.",
    "product.sport-gear-store.name": "Sport Gear Store — Pro",
    "product.sport-gear-store.description":
      "Sport Gear Store — Your professional e-commerce solution, ready to sell. This is a real example of an online store, fast, reliable and designed to maximize conversions. Want to see this design customized with your products and brand? Choose the package that best suits your needs in our Pricing section. Start now: with a proportional deposit based on your chosen package we lock in your offer and put your store online in just a few days.",
    "product.sport-gear-store-premium.name": "Sport Gear Store — Premium",
    "product.sport-gear-store-premium.description":
      "Purchase this package to get a production-ready store with hosting, domain and support options.",
    "product.outlet-clothing.name": "Outlet-Clothing",
    "product.outlet-clothing.description":
      "Outlet-Clothing — Your professional e-commerce solution, ready to sell. This is a real example of an online store, fast, reliable and designed to maximize conversions. Want to see this design customized with your products and brand? Choose the package that best suits your needs in our Pricing section. Start now: with a proportional deposit based on your chosen package we lock in your offer and put your store online in just a few days.",
    "product.glamour-glow.name": "Glamour Glow — Luxury Cosmetics",
    "product.glamour-glow.description":
      "Glamour Glow — Your professional e-commerce solution, ready to sell. This is a real example of an online store, fast, reliable and designed to maximize conversions. Want to see this design customized with your products and brand? Choose the package that best suits your needs in our Pricing section. Start now: with a proportional deposit based on your chosen package we lock in your offer and put your store online in just a few days.",
    "product.salumificio-nebrodi.name":
      "Salumificio Nero dei Nebrodi — Artisanal Cured Meats",
    "product.salumificio-nebrodi.description":
      "Salumificio Nero dei Nebrodi — Your professional e-commerce solution, ready to sell. This is a real example of an online store, fast, reliable and designed to maximize conversions. Want to see this design customized with your products and brand? Choose the package that best suits your needs in our Pricing section. Start now: with a proportional deposit based on your chosen package we lock in your offer and put your store online in just a few days.",
    "product.luxury-boutique.name": "Luxury Boutique — Packages and details",
    "product.luxury-boutique.description":
      "Luxury Boutique — Your professional e-commerce solution, ready to sell. This is a real example of an online store, fast, reliable and designed to maximize conversions. Want to see this design customized with your products and brand? Choose the package that best suits your needs in our Pricing section. Start now: with a proportional deposit based on your chosen package we lock in your offer and put your store online in just a few days.",
    "product.electro-store.name": "Electro Store — Packages and details",
    "product.electro-store.description":
      "Electro Store — Your professional e-commerce solution, ready to sell. This is a real example of an online store, fast, reliable and designed to maximize conversions. Want to see this design customized with your products and brand? Choose the package that best suits your needs in our Pricing section. Start now: with a proportional deposit based on your chosen package we lock in your offer and put your store online in just a few days.",
    "product.hydroflow-depot.name": "Hydroflow Depot — Packages and details",
    "product.hydroflow-depot.description":
      "Hydroflow Depot — Your professional e-commerce solution, ready to sell. This is a real example of an online store, fast, reliable and designed to maximize conversions. Want to see this design customized with your products and brand? Choose the package that best suits your needs in our Pricing section. Start now: with a proportional deposit based on your chosen package we lock in your offer and put your store online in just a few days.",
    "product.oro-verde.name": "Oro Verde — Packages and details",
    "product.oro-verde.description":
      "Oro Verde — Your professional e-commerce solution, ready to sell. This is a real example of an online store, fast, reliable and designed to maximize conversions. Want to see this design customized with your products and brand? Choose the package that best suits your needs in our Pricing section. Start now: with a proportional deposit based on your chosen package we lock in your offer and put your store online in just a few days.",
    "product.pharmacare.name": "PharmaCare — Packages and details",
    "product.pharmacare.description":
      "PharmaCare — Your professional e-commerce solution, ready to sell. This is a real example of an online store, fast, reliable and designed to maximize conversions. Want to see this design customized with your products and brand? Choose the package that best suits your needs in our Pricing section. Start now: with a proportional deposit based on your chosen package we lock in your offer and put your store online in just a few days.",
    "product.nettari-doro.name": "Nettari D'Oro — Packages and details",
    "product.nettari-doro.description":
      "Nettari D'Oro — Your professional e-commerce solution, ready to sell. This is a real example of an online store, fast, reliable and designed to maximize conversions. Want to see this design customized with your products and brand? Choose the package that best suits your needs in our Pricing section. Start now: with a proportional deposit based on your chosen package we lock in your offer and put your store online in just a few days.",
    "product.sapori-etna.name": "Sapori dell'Etna — Packages and details",
    "product.sapori-etna.description":
      "Sapori dell'Etna — Your professional e-commerce solution, ready to sell. This is a real example of an online store, fast, reliable and designed to maximize conversions. Want to see this design customized with your products and brand? Choose the package that best suits your needs in our Pricing section. Start now: with a proportional deposit based on your chosen package we lock in your offer and put your store online in just a few days.",
    "product.sapori-calabria.name": "Sapori di Calabria — Packages and details",
    "product.sapori-calabria.description":
      "Sapori di Calabria — Your professional e-commerce solution, ready to sell. This is a real example of an online store, fast, reliable and designed to maximize conversions. Want to see this design customized with your products and brand? Choose the package that best suits your needs in our Pricing section. Start now: with a proportional deposit based on your chosen package we lock in your offer and put your store online in just a few days.",
    "product.cantina-prestige.name": "Cantina Prestige — Packages and details",
    "product.cantina-prestige.description":
      "Cantina Prestige — Your professional e-commerce solution, ready to sell. This is a real example of an online store, fast, reliable and designed to maximize conversions. Want to see this design customized with your products and brand? Choose the package that best suits your needs in our Pricing section. Start now: with a proportional deposit based on your chosen package we lock in your offer and put your store online in just a few days.",
    "product.forged-performance.name":
      "Forged Performance — Packages and details",
    "product.forged-performance.description":
      "Forged Performance — Your professional e-commerce solution, ready to sell. This is a real example of an online store, fast, reliable and designed to maximize conversions. Want to see this design customized with your products and brand? Choose the package that best suits your needs in our Pricing section. Start now: with a proportional deposit based on your chosen package we lock in your offer and put your store online in just a few days.",
    "product.rosso-corsa.name": "Rosso Corsa Parts — Packages and details",
    "product.rosso-corsa.description":
      "Rosso Corsa Parts — Your professional e-commerce solution, ready to sell. This is a real example of an online store, fast, reliable and designed to maximize conversions. Want to see this design customized with your products and brand? Choose the package that best suits your needs in our Pricing section. Start now: with a proportional deposit based on your chosen package we lock in your offer and put your store online in just a few days.",
    "product.musclefuel.name": "MuscleFuel — Packages and details",
    "product.musclefuel.description":
      "MuscleFuel — Your professional e-commerce solution, ready to sell. This is a real example of an online store, fast, reliable and designed to maximize conversions. Want to see this design customized with your products and brand? Choose the package that best suits your needs in our Pricing section. Start now: with a proportional deposit based on your chosen package we lock in your offer and put your store online in just a few days.",
    "product.tech-haven.name": "Tech Haven — Packages and details",
    "product.tech-haven.description":
      "Tech Haven — Your professional e-commerce solution, ready to sell. This is a real example of an online store, fast, reliable and designed to maximize conversions. Want to see this design customized with your products and brand? Choose the package that best suits your needs in our Pricing section. Start now: with a proportional deposit based on your chosen package we lock in your offer and put your store online in just a few days.",
    "product.happy-paws.name": "Happy Paws — Packages and details",
    "product.happy-paws.description":
      "Happy Paws — Your professional e-commerce solution, ready to sell. This is a real example of an online store, fast, reliable and designed to maximize conversions. Want to see this design customized with your products and brand? Choose the package that best suits your needs in our Pricing section. Start now: with a proportional deposit based on your chosen package we lock in your offer and put your store online in just a few days.",
    "product.urban-threads.name": "Urban Threads — Packages and details",
    "product.urban-threads.description":
      "Urban Threads — Your professional e-commerce solution, ready to sell. This is a real example of an online store, fast, reliable and designed to maximize conversions. Want to see this design customized with your products and brand? Choose the package that best suits your needs in our Pricing section. Start now: with a proportional deposit based on your chosen package we lock in your offer and put your store online in just a few days.",
    "product.levis-outfitters.name": "Levi's Outfitters — Packages and details",
    "product.levis-outfitters.description":
      "Levi's Outfitters — Your professional e-commerce solution, ready to sell. This is a real example of an online store, fast, reliable and designed to maximize conversions. Want to see this design customized with your products and brand? Choose the package that best suits your needs in our Pricing section. Start now: with a proportional deposit based on your chosen package we lock in your offer and put your store online in just a few days.",
    "product.fragrance-boutique.name":
      "Fragrance Boutique — Packages and details",
    "product.fragrance-boutique.description":
      "Fragrance Boutique — Your professional e-commerce solution, ready to sell. This is a real example of an online store, fast, reliable and designed to maximize conversions. Want to see this design customized with your products and brand? Choose the package that best suits your needs in our Pricing section. Start now: with a proportional deposit based on your chosen package we lock in your offer and put your store online in just a few days.",
    "product.explosive-company.name":
      "Explosive Company — Packages and details",
    "product.explosive-company.description":
      "Explosive Company — Your professional e-commerce solution, ready to sell. This is a real example of an online store, fast, reliable and designed to maximize conversions. Want to see this design customized with your products and brand? Choose the package that best suits your needs in our Pricing section. Start now: with a proportional deposit based on your chosen package we lock in your offer and put your store online in just a few days.",

    // template & UI
    "template.not_found": "Template not found.",
    "template.added_to_cart_title": "Added to cart",
    "template.added_to_cart_desc": "{variant} added to cart.",
    "template.add_to_cart": "Add to cart",
    "template.select": "Select {variant}",
    "template.promo_note": "Promotional price — Limited offer",
    "template.savings": "You save {amount}",
    "template.packages_title": "Sport Gear Store — Packages and details",
    "template.packages_intro":
      "This product is a complete package to sell shoes and sports items online. You can choose Basic, Pro or Premium: each includes templates, sales tools and optional services described below.",
    "template.next_steps.title": "Next steps:",
    "template.next_steps.1": "Confirm the package (Basic / Pro / Premium)",
    "template.next_steps.2": "Provide domain and necessary access",
    "template.next_steps.3": "Define payment and shipping preferences",
    "template.next_steps.4": "Proceed with deploy and testing",
    "template.price": "Price",
    "template.toast_conflict_title": "Attention",
    "template.toast_conflict_desc":
      "{conflict} is already in the cart. Remove it before adding another version.",
    "template.case_study_intro":
      "This is a real online store example ready to adapt for your business. It's fast, reliable, and designed to sell. See it with your products and brand? Personalized for your brand and completely yours. Choose the package that fits your needs in our Pricing section and get your store live in just a few days.",
    "template.service_details_title": "Everything Included in Your Package",
    "template.service_details_intro":
      "Complete service from start to finish. We handle everything so you can focus on selling.",
    "template.service_detail.deploy_title": "Online in 15 Working Days",
    "template.service_detail.deploy_desc":
      "Your store is ready to sell. We take care of everything and launch it in about a week.",
    "template.service_detail.setup_title": "Everything Ready to Go",
    "template.service_detail.setup_desc":
      "Your web address, secure payments with cards, and everything protected. We handle it all.",
    "template.service_detail.seo_title": "Found on Google (Base Setup)",
    "template.service_detail.seo_desc":
      "Initial setup so new customers can find you on Google when they search for what you sell.",
    "template.service_detail.maintenance_title":
      "Support After Launch (Optional)",
    "template.service_detail.maintenance_desc":
      "After your store launches, choose our plans to keep it fast and updated. Monthly options available.",
    "template.roi_title": "Why This is a Smart Investment",
    "template.roi_vs_dev":
      "Save over €7,000 compared to building from scratch, without losing quality.",
    "template.roi_vs_platforms":
      "No hidden fees eating into your profits. Every euro you make is yours to keep.",
    "template.roi_guarantee":
      "Your store is super fast and ready to grow with you, even when business booms.",
    "template.book_deploy_cta": "Book Your Store Launch — Start in Days",
    "template.contact_cta": "Questions? Contact Us",

    "preview.saas.title": "{brand} — Your tailored e‑commerce project.",
    "preview.saas.lead":
      "This is a real working project, not a simple demo. It is fast, reliable and designed to sell.",
    "preview.saas.cta_paragraph":
      "Do you want to see this design on your domain? Start now: with a €199 deposit we start personalization and put it online in a few days.",
    "preview.saas.footer":
      "This template becomes the foundation of your turnkey €1700 project (50% limited-time discount): we adapt it to your brand and put it online in just a few days.",

    "booking.title": "Finalize your fast reservation",
    "booking.subtitle":
      "We will use your details to start the contract and consultation. We will contact you shortly.",
    "booking.disclaimer":
      "The commitment fee is non‑refundable and will be deducted from the final balance of your chosen package.",
    "booking.name_label": "Full name",
    "booking.email_label": "Business email",
    "booking.phone_label": "Phone number",
    "booking.domain_label": "Desired domain (e.g. yourbrand.com)",
    "booking.submit": "Proceed to checkout (€199 commitment fee)",
    "booking.submitting": "Redirecting to Stripe…",
    "booking.error_checkout":
      "Unable to start the checkout. Please try again in a few minutes.",
    "booking.error_generic":
      "Unexpected error while starting the checkout. Please try again later.",
    "booking.confirm_message":
      "We charge a non‑refundable commitment fee to reserve your project. This amount will be deducted from the final balance of your chosen package. Do you want to proceed to payment?",

    "partners.title": "Built with the best partners",
    "partners.subtitle":
      "We design and launch your store on a modern, reliable stack with enterprise-grade tools for performance, security, and scale.",

    "variant.sport-gear-store-basic.title": "Basic",
    "variant.sport-gear-store-basic.description":
      "Storefront base with catalog, product template and checkout; ideal to start quickly.",
    "variant.sport-gear-store-basic.features":
      "Storefront base with catalog||Product template & images||Simple checkout||Documentation support",

    "variant.sport-gear-store.title": "Pro",
    "variant.sport-gear-store.description":
      "Everything in Basic plus maintenance, updates, marketing and integrations.",
    "variant.sport-gear-store.features":
      "Everything in Basic||Maintenance & updates||Optimizations & priority support||WhatsApp & social integration||Order confirmation emails",

    "variant.sport-gear-store-premium.title": "Premium",
    "variant.sport-gear-store-premium.description":
      "Full managed package with hosting, domain setup and premium support.",
    "variant.sport-gear-store-premium.features":
      "Everything in Pro||Managed hosting & deploy||Domain, SSL & migration setup||Dedicated admin page for products, images & prices||Initial campaigns & priority support",

    "common.close": "Close",
    "common.cancel": "Cancel",
    "common.select_short": "Select",

    "whatsapp.contact": "Contact me on WhatsApp",
    "whatsapp.label": "Quick Consultation",
    "whatsapp.message":
      "Hi BuildStore! I'd like more information about launching my new e-commerce store.",
    "maintenance.title": "Site under maintenance",
    "maintenance.description":
      "The site is temporarily under maintenance. It will be back online soon.",
    "maintenance.enter_code_placeholder": "Enter access code for testing",
    "maintenance.enter": "Enter",
    "maintenance.exit_btn": "Exit maintenance",
    "maintenance.error.invalid_code": "Invalid code",
    "maintenance.error.enter_code": "Enter access code",
    "error.generic": "Error",
    "error.add_to_cart_failed": "Unable to add to cart",
    "error.download_failed": "Error during download",
    "error.app": "Application error",

    "admin.title": "Admin — Management",
    "admin.login_required": "You must be logged in to access the admin page.",
    "admin.access_denied": "Access restricted",
    "admin.access_denied_desc":
      "Admin access is reserved for customers who purchased the Premium package.",
    "admin.checking": "Checking access...",
    "admin.manage": "Admin — Management",
    "admin.tab.products": "Products",
    "admin.tab.orders": "Orders",
    "admin.loading": "Loading…",
    "admin.add_product": "Add product",
    "admin.search": "Search",

    "success.title": "Project Confirmed",
    "success.thanks":
      "Your booking for the e-commerce project has been successfully secured. You will shortly receive a confirmation email with your order summary and instructions for your next kickoff/briefing appointment with our team.",
    "success.download_msg_basic":
      "Great! You chose {variant}. Download the package with templates and the step‑by‑step installation guide.",
    "success.download_msg_pro":
      "Perfect! You chose {variant}. Download the complete package along with instructions to finish setup and activate integrations.",
    "success.download_msg_premium":
      "Congratulations! You chose {variant}. Download the complete package and detailed installation instructions now.",
    "success.download_msg_other":
      "You purchased {variant}. Download the package and follow the included instructions.",
    "success.download_now": "Download now",
    "success.downloading": "Preparing download...",
    "success.no_product_selected": "No package selected for download",
    "success.back_to_shop": "Back to shop",
    "success.go_admin": "Go to Admin",

    "cancel.title": "Payment cancelled",
    "cancel.desc": "You cancelled the checkout. You can retry anytime.",
    "cancel.back_to_shop": "Back to shop",

    "value.hero.title":
      "Own your platform, double your speed, keep 100% of your profits.",
    "value.hero.subtitle":
      "SaaS platforms charge you to grow. BuildStore.it is your definitive investment in performance, speed and total control.",

    "value.comparison.criteria": "Selection Criterion",
    "value.comparison.shopify": "🛍️ Rental Platforms (Shopify, Wix)",
    "value.comparison.webador": "🔧 Website Builders (Webador)",
    "value.comparison.buildstore":
      "🚀 BuildStore.it (Modern Stack & Total Control)",

    "value.comparison.rows.cost.label": "Cost Model",
    "value.comparison.rows.cost.shopify":
      "Monthly fee + Commissions on every sale.",
    "value.comparison.rows.cost.webador": "Free / €5.50 - €20/month",
    "value.comparison.rows.cost.buildstore":
      "Transparent Pricing (from €299): No monthly fees, zero commissions on sales. You pay for the value, not for the rental.",

    "value.comparison.rows.ownership.label": "Code Ownership",
    "value.comparison.rows.ownership.shopify":
      "The code belongs to the provider (you are renting).",
    "value.comparison.rows.ownership.webador":
      "The code belongs to the provider (you are renting).",
    "value.comparison.rows.ownership.buildstore":
      "100% Ownership: The entire source code is yours. You can move it wherever you want, whenever you want.",

    "value.comparison.rows.technology.label": "Technology & Speed",
    "value.comparison.rows.technology.shopify":
      "Legacy code, medium speed. Server-dependent.",
    "value.comparison.rows.technology.webador":
      "Traditional hosting, limited speed.",
    "value.comparison.rows.technology.buildstore":
      "Advanced Cloud Architecture with Instant Response. Proprietary technology optimized to eliminate every loading delay.",

    "value.comparison.rows.management.label": "Technical Maintenance",
    "value.comparison.rows.management.shopify":
      "You manage apps, updates and bugs.",
    "value.comparison.rows.management.webador": "Automated by the platform.",
    "value.comparison.rows.management.buildstore":
      "Delivered Ready: Platform built and optimized by experts. You sell, we build.",

    "value.comparison.rows.seo.label": "SEO Optimization",
    "value.comparison.rows.seo.shopify": "SEO tools included but limited.",
    "value.comparison.rows.seo.webador": "Basic SEO optimization.",
    "value.comparison.rows.seo.buildstore":
      "Optimized for Core Web Vitals: The fastest speed is Google's first ranking factor. We deliver that.",

    "value.comparison.rows.scalability.label": "Scalability",
    "value.comparison.rows.scalability.shopify":
      "Requires expensive upgrades for large traffic volumes.",
    "value.comparison.rows.scalability.webador": "Limited to medium traffic.",
    "value.comparison.rows.scalability.buildstore":
      "Built to Scale: Architecture that handles millions of visitors without paying for idle servers.",

    "value.comparison.rows.customization.label": "Customization",
    "value.comparison.rows.customization.shopify":
      "Limited to themes and app ecosystem.",
    "value.comparison.rows.customization.webador": "Limited to templates.",
    "value.comparison.rows.customization.buildstore":
      "Unlimited: 100% customizable design and functionality (thanks to modern stack).",

    "value.conclusion.title": "Stop Renting. Start Owning.",
    "value.conclusion.text":
      "Why spend hundreds of euros in fees and commissions to Shopify when you can invest that money once in a proprietary platform, infinitely faster and scalable? BuildStore.it gives you the technical excellence that builders cannot offer. Your success is yours, not ours.",

    "value.faq.title": "Frequently Asked Questions",

    "value.faq.questions.0.q":
      "What are the recurring costs after my deposit payment?",
    "value.faq.questions.0.a":
      "After the initial setup, you don't pay any monthly fees to us. The only recurring costs relate to the maintenance of the global infrastructure and database, which you pay directly to the infrastructure providers based on actual usage. Thanks to our Cloud-Native Architecture, these costs are minimal (often near zero for small/medium volumes) and only scale alongside your success. You save thousands of euros per year by eliminating the fixed fees and commissions of traditional platforms.",

    "value.faq.questions.1.q": "Is the platform and code mine?",
    "value.faq.questions.1.a":
      "Absolutely yes. Unlike Wix or Shopify, you are 100% owner of the source code and all data. You have complete control and unlimited access to your digital asset.",

    "value.faq.questions.2.q":
      "What exactly is the BuildStore technology and why is it faster than standard systems?",
    "value.faq.questions.2.a":
      "We use a next-generation cloud-native infrastructure that separates design from business logic. Unlike traditional systems (Shopify/Wix) that rely on heavy centralized servers, our technology distributes your store across an intelligent global network. This means your store doesn't have to 'wait' for a distant server: it's already present wherever your customer is located, guaranteeing speed that standard systems cannot physically achieve.",

    "value.faq.questions.3.q": "Can BuildStore.it handle high traffic spikes?",
    "value.faq.questions.3.a":
      "Yes. Our architecture is built to scale horizontally automatically. Your platform can easily handle millions of requests and sudden traffic spikes without worrying about crashes or server upgrades. We've engineered it to stay fast even under extreme load.",

    "value.faq.questions.4.q":
      "Do I need technical knowledge to manage the store daily?",
    "value.faq.questions.4.a":
      "Not at all. We deliver your store with an intuitive dashboard (CMS) where you can manage products, orders, and content as easily as on any other platform. The difference? Your site is 10x faster.",

    "value.faq.questions.5.q":
      "What if I want to add complex functionality in the future?",
    "value.faq.questions.5.a":
      "Since the platform is entirely code-based and not locked in an app store (like Shopify), customization is unlimited. We can build any complex functionality without hitting the limits of standard builders. Your platform is as flexible as your ambition grows.",

    "value.faq.questions.6.q": "How does the deployment process work?",
    "value.faq.questions.6.a":
      "Once your design is approved, we activate your proprietary instance on our global infrastructure. The 'go live' moment is instant: we coordinate domain propagation and security protocols, delivering you the keys to a platform that's already configured, secure, and ready to sell.",

    "value.faq.questions.7.q": "What support do I get after launch?",
    "value.faq.questions.7.a":
      "After launch, you can activate our managed hosting and maintenance plans starting from a monthly fee. We also provide technical support to help you optimize and grow your business.",

    "value.cta.title": "Ready to Own Your Platform?",
    "value.cta.text":
      "Stop paying monthly subscriptions. Invest in BuildStore.it and own your digital asset forever.",
    "value.cta.button": "Schedule Your Consultation",

    // ⭐ NUOVE TRADUZIONI PER LA PAGINA /valore
    "value.meta.description":
      "BuildStore.it: Beyond Shopify & Wix | Proprietary E-commerce",
    "value.meta.title":
      "BuildStore.it - Value: Beyond Shopify & Wix | Proprietary E-commerce",

    // Blog articles - English
    "blog.core-web-vitals.title":
      "Core Web Vitals 2025: The Quick Guide to Achieving 90+ on Google PageSpeed 🚀",
    "blog.core-web-vitals.description":
      "Get the maximum score on PageSpeed Insights by optimizing LCP, INP and CLS. Practical and fast guide to increase conversions.",
    "blog.schema-markup.title":
      "Quick Schema Markup: Your Guide to Getting Stars and Prices on Google ⭐",
    "blog.schema-markup.description":
      "Concise guide to implement the 4 essential Schemas (Product, Rating, Breadcrumb) and get Rich Snippets that increase your CTR by 30%.",
    "blog.cdn-edge-network.title":
      "CDN, Edge Network and Lazy Loading: The BuildStore Triad for Beating Load Times ⚡",
    "blog.cdn-edge-network.description":
      "The modern approach to speed: how Edge Network (Vercel) and Lazy Loading guarantee LCP < 2s and beat slow architectures.",

    // Blog Access Gate
    "blog.access.title": "Premium Blog Access",
    "blog.access.subtitle":
      "Unlock premium insights, strategies, and proven tactics worth thousands in consulting fees.",
    "blog.access.benefit1.title": "Expert Knowledge",
    "blog.access.benefit1.desc":
      "Industry best practices and proven strategies",
    "blog.access.benefit2.title": "Lifetime Access",
    "blog.access.benefit2.desc": "One-time payment for permanent access",
    "blog.access.benefit3.title": "Regular Updates",
    "blog.access.benefit3.desc": "New content and insights added monthly",
    "blog.access.payment_note": "One-time payment for lifetime access",
    "blog.access.form.fullname": "Full Name",
    "blog.access.form.fullname_placeholder": "Your name",
    "blog.access.form.email": "Email Address",
    "blog.access.form.email_placeholder": "your@email.com",
    "blog.access.form.phone": "Phone Number",
    "blog.access.form.phone_placeholder": "+39 ...",
    "blog.access.form.submit": "Unlock Premium Blog Access",
    "blog.access.form.processing": "Processing...",
    "blog.access.form.security":
      "Secure payment processed. Your data is protected and never shared.",
    "blog.access.faq.title": "Questions?",
    "blog.access.faq.q1": "What do I get?",
    "blog.access.faq.a1":
      "Complete access to all premium blog articles, case studies, and optimization guides.",
    "blog.access.faq.q2": "Is this a subscription?",
    "blog.access.faq.a2":
      "No. One payment gives you lifetime access. No recurring charges.",
    "blog.access.faq.q3": "What payment methods do you accept?",
    "blog.access.faq.a3":
      "We accept all major credit cards via secure payment processing.",
    "blog.access.success":
      "Welcome to premium blog access! You now have lifetime access to all our premium insights and strategies.",
    "blog.access.success_cta": "Go to Blog",

    "case_studies.title": "Our Case Studies",
    "case_studies.description":
      "Explore real, working examples of e-commerce platforms we've built for businesses across multiple industries. See the power of BuildStore in action.",
    "case_studies.cta_title": "Ready to Build Your Platform?",
    "case_studies.cta_description":
      "Let's create something amazing together. Contact us to discuss your e-commerce vision.",
    "case_studies.cta_button": "Start Your Project",

    "case_study.challenge": "Challenge",
    "case_study.solution": "Solution",
    "case_study.results": "Results",
    "case_study.modern_stack": "Modern, Enterprise-Grade Technology",
    "case_study.modern_stack_description":
      "Built on cutting-edge serverless architecture with global CDN, scalable databases, and security-first infrastructure. Not a template platform—a fully custom, proprietary solution designed for performance and growth.",
    "case_study.modern_stack_speed": "Lightning Fast",
    "case_study.modern_stack_speed_desc":
      "Optimized for Core Web Vitals with sub-2s load times. Every millisecond matters for conversions.",
    "case_study.modern_stack_secure": "Enterprise Security",
    "case_study.modern_stack_secure_desc":
      "SSL/TLS encryption, automatic backups, and 24/7 monitoring. Your data and customer data are protected.",
    "case_study.modern_stack_scalable": "Infinitely Scalable",
    "case_study.modern_stack_scalable_desc":
      "Handle traffic spikes automatically. Architecture that grows with your business without expensive upgrades.",
    "case_study.modern_stack_owned": "100% Yours",
    "case_study.modern_stack_owned_desc":
      "Full code ownership. No vendor lock-in, no hidden commissions. Your digital asset, forever.",
    "case_study.visit_title": "Explore the Live Platform",
    "case_study.visit_button": "Visit Live Site",
    "case_study.click_to_visit": "Click to visit the site",
    "case_study.explore_more": "Explore More Case Studies",
    "case_study.back_to_all": "View All Cases",
    "case_study.back_to_cases": "Back to Case Studies",
    "case_study.breadcrumb_cases": "Case Studies",
    "case_study.not_found_title": "Case Study Not Found",
    "case_study.not_found_description":
      "Sorry, we couldn't find the case study you're looking for.",

    // Sicilian Bio Food Case Study Content
    "case_study.sicilianbiofood.description":
      "A premium e-commerce platform for authentic Sicilian organic and artisanal food products. Designed to showcase the richness of traditional recipes with a modern digital experience.",
    "case_study.sicilianbiofood.challenge":
      "The client needed a robust online presence to reach customers across Italy and Europe, while maintaining the authentic storytelling of their organic food heritage. They required a platform that could handle seasonal product variations and highlight the quality of their offerings.",
    "case_study.sicilianbiofood.solution":
      "We developed a high-performance e-commerce solution built on modern serverless architecture. The platform features stunning product galleries with rich descriptions, integrated payment processing, inventory management, and a seamless checkout experience optimized for food retail. The design emphasizes the brand's commitment to quality and tradition.",
    "case_study.sicilianbiofood.results":
      "The platform is ready to drive growth. With its official launch, Sicilian Bio Food can reach customers throughout Italy and Europe, expanding its customer base from local to international and maximizing online sales.",

    "checkout.deposit_message":
      "Project reservation deposit. The amount will be deducted from the final balance of the chosen package.",

    "services.seo.name": "SEO Optimization",
    "services.seo.price": "Starting from €400",
    "services.seo.description":
      "Comprehensive SEO setup including audit, optimization, and Google analytics configuration.",

    "services.migration.name": "Platform Migration / Custom Development",
    "services.migration.price": "Starting from €400",
    "services.migration.description":
      "Migration from other platforms or custom functionality development for your specific needs.",

    "services.multilingual.name": "Market Expansion (Multilingual)",
    "services.multilingual.price": "Starting from €450 per language",
    "services.multilingual.description":
      "Internationalize your store with multi-language support and SEO optimization.",

    "checkout.ecommerce_deposit_message":
      "Project reservation deposit. The amount will be deducted from the final balance of the chosen package.",
    "checkout.service_full_payment_message":
      "Full payment for the selected service. Professional solution ready in 15 working days.",
    "checkout.showcase_full_payment_message":
      "Full payment for professional showcase website. Ready in 15 working days.",
  },
  it: {
    "header.subtitle": "Soluzioni e‑commerce ad alte prestazioni",
    "header.go_home_aria": "Vai alla homepage",
    "header.homepage_title": "Homepage",
    "country.en": "Inghilterra",
    "country.it": "Italia",
    "home.h1":
      "BuildStore: Sviluppo E-commerce | Negozio Online. Soluzione Chiavi in Mano.",
    "home.p":
      "Lancia il tuo store online 100% proprietario a partire da 699€. Zero abbonamenti, zero commissioni: la tua piattaforma definitiva, tua per sempre. Infrastruttura impeccabile ottimizzata per le vendite. Smetti di pagare l'affitto per il tuo business, investi nel tuo futuro.",
    "home.view_products": "Sfoglia i Template",
    "home.contact_sales": "Contattaci",
    "home.book_deploy": "Scegli il Tuo Pacchetto",
    "home.book_deploy_sub": "15gg Lavorativi • Tuo per Sempre",
    "feature.performance.title": "Ultra Veloce",
    "feature.performance.desc":
      "Massima Velocità di Caricamento. Meno Abbandoni, Miglior Posizionamento su Google.",
    "feature.security.title": "Sicuro & Protetto",
    "feature.security.desc":
      "Sicurezza e Affidabilità Garantite. Gestiamo transazioni protette con crittografia SSL/TLS e backup automatici. I tuoi dati e quelli dei clienti sono monitorati 24/7.",
    "feature.integrations.title": "Integrazioni per un E-commerce Completo.",
    "feature.integrations.desc":
      "Accetta pagamenti facili e sicuri (carte, PayPal, Stripe), gestisci spedizioni con i principali corrieri e sincronizza il tuo magazzino.",
    "packages.title": "Scegli il Tuo E-commerce e Iniziamo",
    "packages.subtitle":
      "Ogni esempio è un negozio online che personalizzeremo per il tuo brand.",
    "packages.open": "Guarda l'anteprima",
    "packages.building": "In arrivo",
    "packages.long_description":
      "Lo personalizziamo completamente e lo mettiamo online in 15 giorni. Soluzioni professionali a partire da €699.",
    "packages.card_description":
      "Caso studio e‑commerce completo per il commercio sportivo.",
    "home.portfolio_label": "Esempi reali del nostro lavoro",
    "home.portfolio_projects":
      "Esempi di negozi che funzionano davvero. Vedi esattamente come sarà il tuo prima ancora che iniziamo.",
    "home.portfolio_details":
      "Ogni esempio include tutto: dominio, pagamenti sicuri e SEO. Tutto incluso nel pacchetto scelto. Scopri le opzioni nella sezione Prezzi.",
    "contact.title": "Costruiamo il Tuo E-commerce",
    "contact.p":
      "Offriamo diverse soluzioni e-commerce personalizzate secondo le tue esigenze. Che tu stia cercando un pacchetto chiavi in mano o servizi specifici, discutiamo insieme quale opzione è perfetta per il tuo business.",
    "contact.contact_sales": "Contattaci Ora",
    "contact.admin": "Admin",
    "contact.pricing_link": "Vedi i Nostri Prezzi",

    "contact.form.name": "Nome Completo",
    "contact.form.name_placeholder": "Il tuo nome",
    "contact.form.email": "Indirizzo Email",
    "contact.form.email_placeholder": "tua@email.com",
    "contact.form.phone": "Numero di Telefono",
    "contact.form.phone_placeholder": "+39 ...",
    "contact.form.optional": "opzionale",
    "contact.form.message": "Messaggio",
    "contact.form.message_placeholder": "Parlaci del tuo progetto...",
    "contact.form.submit": "Invia Messaggio",
    "contact.form.sending": "Invio in corso...",
    "contact.form.success.title": "Messaggio Inviato",
    "contact.form.success.desc": "Grazie! Ti contatteremo presto.",
    "contact.form.error.title": "Errore",
    "contact.form.error.send_failed":
      "Errore durante l'invio. Riprova più tardi.",
    "contact.form.error.required": "Campi Obbligatori",
    "contact.form.error.fill_fields": "Compila tutti i campi obbligatori.",
    "contact.alternative_text": "Preferisci chattare?",
    "contact.whatsapp_cta": "Contattaci via WhatsApp",
    "contact.form.label": "Rimaniamo in Contatto",
    "contact.form.description":
      "Compila il modulo qui sotto e ti risponderemo entro 24 ore.",
    "footer.contact_button": "Contattaci",

    "home.why_buildstore_title":
      "Perché BuildStore rispetto a Shopify e Webador?",
    "home.why_buildstore_desc":
      "Scopri come il nostro stack moderno (Vercel, Supabase, Cloudflare) ti offre velocità, proprietà e scalabilità che i builder standard non possono darti.",
    "home.read_comparison": "Leggi il Confronto Completo",

    "about.hero.title": "Chi Siamo — Un Approccio Diverso all'E-commerce",
    "about.hero.subtitle":
      "Crediamo che ogni business meriti un negozio online veloce, affidabile e davvero proprio. Per questo abbiamo creato BuildStore.",
    "about.mission.title": "La Nostra Missione",
    "about.mission.desc":
      "Liberare i negozi online dagli abbonamenti mensili e dalle commissioni nascoste. Ti diamo il pieno controllo della tua piattaforma, trasparenza assoluta nei prezzi e la velocità che Google premia.",
    "about.mission.quote":
      "Il tuo negozio è tuo, per sempre. Niente abbonamenti, niente commissioni, niente limiti.",
    "about.why.title": "Perché Facciamo Questo",
    "about.why.desc":
      "Troppi imprenditori sono intrappolati in piattaforme SaaS che prendono una percentuale su ogni vendita limitando la loro crescita. Volevamo creare un'alternativa: una soluzione moderna, veloce e veramente proprietaria che ti permetta di concentrarti su cosa sai fare meglio: vendere.",
    "about.why.quote":
      "Costruisci una volta, possiedi per sempre. Questa è la promessa di BuildStore.",
    "about.tech.title": "Costruito su Tecnologie Moderne",
    "about.tech.desc":
      "Utilizziamo l'infrastruttura serverless e edge-computing più avanzata per garantire che il tuo negozio sia sempre veloce, sicuro e scalabile.",
    "about.tech.vercel": "CDN globale per consegne ultra-veloci",
    "about.tech.supabase": "Database PostgreSQL sicuro e scalabile",
    "about.tech.cloudflare": "Edge computing e protezione DDoS",
    "about.tech.stripe": "Pagamenti affidabili e sicuri",
    "about.values.title": "I Nostri Valori Fondamentali",
    "about.values.speed_title": "Velocità",
    "about.values.speed_desc":
      "Ogni millisecondo conta. Ottimizziamo per le Core Web Vitals e il ranking su Google.",
    "about.values.ownership_title": "Proprietà",
    "about.values.ownership_desc":
      "Il 100% del tuo codice e dei tuoi dati è tuo. Niente vendor lock-in, niente sorprese.",
    "about.values.scalability_title": "Scalabilità",
    "about.values.scalability_desc":
      "Costruito per crescere con te, dalla tua prima vendita a milioni di clienti.",
    "about.cta.title": "Pronto a Possedere la Tua Piattaforma?",
    "about.cta.desc":
      "Unisciti alle centinaia di business che hanno scelto BuildStore.",
    "about.cta.button": "Inizia il Tuo Progetto",

    "index.loading": "Caricamento prodotti...",
    "index.error_title": "Errore nel caricamento dei prodotti",
    "index.btn_retry": "Riprova",
    "index.btn_test_server": "Test server",
    "index.btn_use_fallback": "Usa server alternativo",
    "index.suggestion":
      "Apri la Console (DevTools) → Network per vedere lo stato della richiesta a /api/products. Se vedi 404/502 o CORS, il backend non è raggiungibile dal dominio corrente.",
    "index.hero.title":
      "Il Nostro Portfolio: Esempi Reali di E-commerce Vincenti",
    "index.hero.desc":
      "Esplora i negozi online ad alta performance che abbiamo sviluppato per moda, food, cosmetici e altri settori. Ogni progetto è una soluzione e-commerce funzionale e personalizzabile per il tuo brand, pronta a generare vendite. Scegli il pacchetto più adatto alle tue esigenze, con soluzioni professionali a partire da €699.",

    "footer.email": "Email",
    "footer.phone": "Telefono",
    "footer.phone_hidden": "+39 350 593 3542",
    "footer.address": "Indirizzo",
    "footer.links.home": "Home",
    "footer.links.products": "Progetti",
    "footer.links.valore": "Valore",
    "footer.links.about": "Chi Siamo",
    "footer.links.contact": "Contattaci",
    "footer.links.faq": "Domande Frequenti",
    "footer.links.admin": "Admin",
    "nav.projects": "Progetti",
    "nav.pricing": "Prezzi",
    "nav.blog": "Blog",
    "nav.value": "Valore",
    "nav.about": "Chi Siamo",
    "nav.case_studies": "Casi Studio",
    "nav.contact": "Contattaci",

    "pricing.hero.title": "Prezzi Trasparenti e Competitivi",
    "pricing.hero.description":
      "Soluzioni e-commerce professionali a prezzi onesti. Niente abbonamenti nascosti, niente commissioni. Tutto tuo per sempre.",
    "pricing.main_packages": "Pacchetti Principali",
    "pricing.additional_services": "Servizi Aggiuntivi",
    "pricing.faq_title": "Domande Frequenti",
    "pricing.most_popular": "Più Popolare",
    "pricing.cta_title": "Pronto a Iniziare?",
    "pricing.cta_description":
      "Contattaci per una consulenza gratuita e scopri qual è il pacchetto perfetto per te.",
    "pricing.cta_button": "Prenota una Consulenza",
    "pricing.cta_whatsapp": "Chat su WhatsApp",

    "pricing.package.showcase.name": "Sito Vetrina",
    "pricing.package.showcase.description": "Showcase il tuo business online",
    "pricing.package.showcase.cta": "Richiedi preventivo",

    "pricing.package.spa.name": "E-commerce SPA",
    "pricing.package.spa.description": "Un solo prodotto con carrello",
    "pricing.package.spa.cta": "Inizia ora",

    "pricing.package.20.name": "E-commerce 20 prodotti",
    "pricing.package.20.description": "Catalogo piccolo completo",
    "pricing.package.20.cta": "Seleziona",

    "pricing.package.50.name": "E-commerce 50 prodotti",
    "pricing.package.50.description": "Catalogo medio completo",
    "pricing.package.50.cta": "Seleziona",

    "pricing.package.200.name": "E-commerce 50-200 prodotti",
    "pricing.package.200.description":
      "Catalogo esteso con funzionalità avanzate",
    "pricing.package.200.cta": "Richiedi",

    "pricing.package.enterprise.name": "E-commerce 200+ prodotti",
    "pricing.package.enterprise.description":
      "Soluzioni enterprise personalizzate",
    "pricing.package.enterprise.price": "Su preventivo",
    "pricing.package.enterprise.cta": "Contatta",

    "pricing.service.hosting.name": "Hosting + Dominio",
    "pricing.service.hosting.description":
      "Hosting gestito su infrastrutture scalabili",
    "pricing.service.hosting.cta": "Aggiungi",

    "pricing.service.seo.name": "SEO Base",
    "pricing.service.seo.description":
      "Ottimizzazione iniziale per motori di ricerca",
    "pricing.service.seo.cta": "Aggiungi",

    "pricing.service.maintenance.name": "Manutenzione Mensile",
    "pricing.service.maintenance.description":
      "Assistenza continuativa e bug fix",
    "pricing.service.maintenance.cta": "Sottoscrivi",

    "pricing.service.migration.name": "Migrazione Piattaforma",
    "pricing.service.migration.description": "Migrazione da altre piattaforme",
    "pricing.service.migration.cta": "Preventivo",

    "pricing.service.bugfix.name": "Bug Fix Urgenti",
    "pricing.service.bugfix.description": "Risoluzione rapida di problemi",
    "pricing.service.bugfix.cta": "Contatta",

    "pricing.service.multilingual.name": "Supporto Multilingua",
    "pricing.service.multilingual.description":
      "Internazionalizza il tuo store con traduzioni integrate",
    "pricing.service.multilingual.cta": "Aggiungi",

    "pricing.faq.q1": "Posso aggiornare il mio piano in futuro?",
    "pricing.faq.a1":
      "Sì, puoi sempre aggiornare il tuo e-commerce aggiungendo più prodotti o funzionalità. Elaboriamo con te il costo della migrazione.",
    "pricing.faq.q2": "Sono inclusi gli aggiornamenti di sicurezza?",
    "pricing.faq.a2":
      "Sì, gli aggiornamenti di sicurezza sono sempre inclusi. Se sottoscrivi manutenzione mensile, gestisce anche le dipendenze.",
    "pricing.faq.q3": "Cosa succede dopo 30 giorni di assistenza post-lancio?",
    "pricing.faq.a3":
      "Hai ancora accesso pieno al tuo store. Puoi sottoscrivere manutenzione mensile (79€) per supporto continuativo, oppure contattarci per interventi specifici (50€/ora).",
    "pricing.faq.q4": "Posso trasferire il mio sito in futuro?",
    "pricing.faq.a4":
      "Sì, il codice è completamente tuo. Puoi portarlo su qualsiasi server o provider che supporti Node.js + Supabase.",
    "pricing.faq.q5": "Quali metodi di pagamento accettate?",
    "pricing.faq.a5":
      "Accettiamo Stripe, bonifico bancario, PayPal. Contattaci per discutere i termini di pagamento.",
    "pricing.faq.q6": "Offrite sconti per progetti a lungo termine?",
    "pricing.faq.a6":
      "Sì, contattaci per progetti annuali o multi-anni. Possiamo discutere sconti volumetrici e piani personalizzati.",

    // Pricing package features - Sito Vetrina
    "pricing.package.showcase.features.0": "Sito responsive moderno",
    "pricing.package.showcase.features.1": "5-10 pagine statiche",
    "pricing.package.showcase.features.2": "Dominio incluso (primo anno)",
    "pricing.package.showcase.features.3": "SSL certificato",
    "pricing.package.showcase.features.4": "Contatti di base",
    "pricing.package.showcase.features.5": "SEO on-page",
    "pricing.package.showcase.features.6":
      "Velocità di caricamento ottimizzata",

    // Pricing package features - E-commerce SPA
    "pricing.package.spa.features.0": "Sito e-commerce completo",
    "pricing.package.spa.features.1": "1 prodotto con varianti",
    "pricing.package.spa.features.2": "Carrello e checkout",
    "pricing.package.spa.features.3": "Integrazione Stripe",
    "pricing.package.spa.features.4": "Notifiche email",
    "pricing.package.spa.features.5": "Dashboard admin base",
    "pricing.package.spa.features.6": "Database sicuro",
    "pricing.package.spa.features.7": "Dominio incluso (primo anno)",

    // Pricing package features - E-commerce 20
    "pricing.package.20.features.0": "Catalogo fino a 20 prodotti",
    "pricing.package.20.features.1": "Filtri e ricerca avanzata",
    "pricing.package.20.features.2": "Carrello e checkout",
    "pricing.package.20.features.3": "Integrazione Stripe",
    "pricing.package.20.features.4": "Sistema di ordini",
    "pricing.package.20.features.5": "Dashboard admin completa",
    "pricing.package.20.features.6": "Email notifiche automiche",
    "pricing.package.20.features.7": "Database scalabile",
    "pricing.package.20.features.8": "Performance ottimizzate",
    "pricing.package.20.features.9": "Dominio incluso (primo anno)",
    "pricing.package.20.features.10": "30 giorni di assistenza post-lancio",

    // Pricing package features - E-commerce 50
    "pricing.package.50.features.0": "Catalogo fino a 50 prodotti",
    "pricing.package.50.features.1": "Filtri e ricerca avanzata",
    "pricing.package.50.features.2": "Carrello e checkout",
    "pricing.package.50.features.3": "Integrazione Stripe",
    "pricing.package.50.features.4": "Sistema di ordini",
    "pricing.package.50.features.5": "Dashboard admin completa",
    "pricing.package.50.features.6": "Email notifiche automiche",
    "pricing.package.50.features.7": "Database scalabile",
    "pricing.package.50.features.8": "Performance ottimizzate",
    "pricing.package.50.features.9": "Dominio incluso (primo anno)",
    "pricing.package.50.features.10": "30 giorni di assistenza post-lancio",

    // Pricing package features - E-commerce 200
    "pricing.package.200.features.0": "Catalogo 50-200 prodotti",
    "pricing.package.200.features.1": "Gestione inventario avanzata",
    "pricing.package.200.features.2": "Filtri multi-parametro",
    "pricing.package.200.features.3": "Sistema di varianti completo",
    "pricing.package.200.features.4": "Checkout ottimizzato",
    "pricing.package.200.features.5": "Integrazione Stripe payment intents",
    "pricing.package.200.features.6": "Analytics base",
    "pricing.package.200.features.7": "Dashboard admin avanzata",
    "pricing.package.200.features.8": "Supporto SMTP integrato",
    "pricing.package.200.features.9": "Database con storage ottimizzato",
    "pricing.package.200.features.10": "Performance e SEO ottimali",
    "pricing.package.200.features.11": "Dominio incluso (primo anno)",
    "pricing.package.200.features.12": "30 giorni di assistenza post-lancio",

    // Pricing package features - E-commerce Enterprise
    "pricing.package.enterprise.features.0":
      "Catalogo illimitato (200+ prodotti)",
    "pricing.package.enterprise.features.1": "Gestione inventario completa",
    "pricing.package.enterprise.features.2":
      "Filtri avanzati e ricerca full-text",
    "pricing.package.enterprise.features.3": "Sistema di categorie annidate",
    "pricing.package.enterprise.features.4": "Varianti prodotto illimitate",
    "pricing.package.enterprise.features.5":
      "Checkout avanzato con logica personalizzata",
    "pricing.package.enterprise.features.6":
      "Integrazioni personalizzate su richiesta",
    "pricing.package.enterprise.features.7": "Dashboard admin enterprise",
    "pricing.package.enterprise.features.8":
      "Analitiche dettagliate e reporting",
    "pricing.package.enterprise.features.9": "Performance monitoring",
    "pricing.package.enterprise.features.10": "Supporto tecnico prioritario",
    "pricing.package.enterprise.features.11": "Dominio incluso (primo anno)",
    "pricing.package.enterprise.features.12": "Assistenza estesa post-lancio",

    // Pricing service features - Hosting
    "pricing.service.hosting.features.0": "Hosting enterprise incluso",
    "pricing.service.hosting.features.1": "Gestione database inclusa",
    "pricing.service.hosting.features.2": "SSL e certificati gestiti",
    "pricing.service.hosting.features.3": "CDN globale",
    "pricing.service.hosting.features.4": "Uptime 99.9%",
    "pricing.service.hosting.features.5": "Backup automatici",
    "pricing.service.hosting.features.6": "Supporto tecnico incluso",

    // Pricing service features - SEO
    "pricing.service.seo.features.0": "Audit SEO completo",
    "pricing.service.seo.features.1": "Meta tags ottimizzazione",
    "pricing.service.seo.features.2": "Sitemap e robots.txt",
    "pricing.service.seo.features.3": "Schema markup implementato",
    "pricing.service.seo.features.4": "Performance optimization",
    "pricing.service.seo.features.5": "Analitiche Google setup",
    "pricing.service.seo.features.6": "Consulenza keyword base",

    // Pricing service features - Maintenance
    "pricing.service.maintenance.features.0": "Bug fix e correzioni",
    "pricing.service.maintenance.features.1": "Piccoli aggiornamenti",
    "pricing.service.maintenance.features.2": "Monitoraggio sicurezza",
    "pricing.service.maintenance.features.3": "Backup e recovery",
    "pricing.service.maintenance.features.4": "Supporto tecnico prioritario",
    "pricing.service.maintenance.features.5": "Update dipendenze",
    "pricing.service.maintenance.features.6": "Performance tuning",

    // Pricing service features - Migration
    "pricing.service.migration.features.0": "Analisi della struttura attuale",
    "pricing.service.migration.features.1": "Import dati e prodotti",
    "pricing.service.migration.features.2": "Gestione redirect URL",
    "pricing.service.migration.features.3": "Testing e QA completo",
    "pricing.service.migration.features.4": "Supporto durante transizione",
    "pricing.service.migration.features.5": "Training per il tuo team",
    "pricing.service.migration.features.6":
      "Valutazione complessità personalizzata",

    // Pricing service features - Bug Fixes
    "pricing.service.bugfix.features.0": "Risposta rapida (< 2 ore)",
    "pricing.service.bugfix.features.1": "Priorità sulla risoluzione",
    "pricing.service.bugfix.features.2": "Documentazione inclusa",
    "pricing.service.bugfix.features.3": "Testing e deploy",
    "pricing.service.bugfix.features.4": "Fatturazione precisa a minuto",
    "pricing.service.bugfix.features.5": "Supporto telefonico disponibile",

    // Pricing service features - Multilingual Support
    "pricing.service.multilingual.features.0":
      "Implementazione struttura URL multilingua",
    "pricing.service.multilingual.features.1":
      "Gestione contenuti in Italiano + altre lingue",
    "pricing.service.multilingual.features.2":
      "Traduzione interfaccia e testi statici",
    "pricing.service.multilingual.features.3":
      "Interfaccia per gestire traduzioni prodotti",
    "pricing.service.multilingual.features.4":
      "Ottimizzazione SEO multilingua (Hreflang tags)",
    "pricing.service.multilingual.features.5":
      "Supporto lingue: Inglese, Francese, Spagnolo, Tedesco, Portoghese",

    "footer.description":
      "Negozi online personalizzati che funzionano velocemente e sono sicuri. Li costruiamo rapidamente, con pagamenti protetti, e funzionano alla perfezione.",
    "home.p_short":
      "Negozi online personalizzati disegnati per il tuo business. Il tuo negozio, il tuo modo.",
    "footer.contact_form": "Modulo Contatti",
    "footer.quick_links": "Link rapidi",
    "footer.copyright": "Tutti i diritti riservati.",

    "auth.login": "Accedi",
    "auth.logout": "Esci",

    placeholder: "Placeholder",

    "product.preview": "Anteprima",
    "product.preview_note": "Anteprima — clicca per aprire",
    "product.case_study_label": "Esempio reale che funziona",
    "product.case_study_tagline":
      "Piattaforma e‑commerce chiavi in mano per il tuo brand",
    "product.building": "Building...",
    "product.building_note": "Building... — non aggiungibile",
    "product.add_to_cart": "Aggiungi al carrello",
    "product.view_details": "Vedi dettagli",

    "cart.open": "Apri carrello",
    "cart.close": "Chiudi",
    "cart.empty": "Nessun articolo nel carrello.",
    "cart.remove": "Rimuovi",
    "cart.total": "Totale",
    "cart.clear": "Svuota",
    "cart.checkout": "Checkout",
    "cart.shipping_title": "Indirizzo di spedizione",
    "cart.placeholder.name": "Nome completo",
    "cart.placeholder.address": "Via, numero",
    "cart.placeholder.city": "Città",
    "cart.placeholder.postal": "CAP",
    "cart.placeholder.country": "Paese",
    "cart.error.login_required":
      "Devi essere loggato per procedere al checkout",
    "cart.error.fill_shipping": "Compila tutti i campi di spedizione",
    "cart.error.checkout_failed": "Errore durante il checkout",
    "cart.processing": "Elaborazione...",
    "cart.proceed": "Procedi al pagamento",

    "login.title.login": "Accedi",
    "login.title.register": "Registrati",
    "login.title.reset": "Reimposta password",
    "login.desc.login": "Inserisci email e password per continuare.",
    "login.desc.register": "Crea un account inserendo i tuoi dati di contatto.",
    "login.desc.reset":
      "Reimposta la password inserendo la tua email. Verrà impostata temporaneamente a '123456789'.",
    "login.placeholder.email": "Email",
    "login.placeholder.password": "Password",
    "login.placeholder.name": "Nome e cognome",
    "login.placeholder.phone": "Numero di cellulare",
    "login.placeholder.address": "Via, numero",
    "login.placeholder.city": "Città",
    "login.placeholder.postal": "CAP",
    "login.placeholder.country": "Paese",
    "login.btn.cancel": "Annulla",
    "login.links.no_account": "Non hai un account? Registrati",
    "login.links.forgot": "Password dimenticata?",
    "login.links.have_account": "Hai già un account? Accedi",
    "login.links.back_to_login": "Torna al login",
    "login.info.reset":
      "Password reimpostata a '123456789'. Usa questa password per accedere.",
    "login.error.fill_register": "Compila nome, cellulare e indirizzo",
    "login.error.login_failed": "Errore durante il login",
    "login.error.register_failed": "Errore durante la registrazione",
    "login.error.reset_failed": "Errore durante il reset",

    // product specific translations (italian)
    "product.sport-gear-store-basic.name": "Sport Gear Store — Basic",
    "product.sport-gear-store-basic.description":
      "Store base pronto per vendere con catalogo, template e checkout; ideale per iniziare rapidamente.",
    "product.sport-gear-store.name": "Sport Gear Store — Pro",
    "product.sport-gear-store.description":
      "Sport Gear Store — Il tuo e-commerce professionale, pronto a vendere. Questo è un vero esempio di negozio online, veloce, affidabile e progettato per massimizzare le conversioni. Vuoi vedere questo design personalizzato con i tuoi prodotti e il tuo brand? Scegli il pacchetto più adatto alle tue esigenze nella sezione Prezzi. Inizia subito: con un acconto proporzionale al pacchetto scelto blocchiamo l'offerta e mettiamo il tuo store online in pochi giorni.",
    "product.sport-gear-store-premium.name": "Sport Gear Store — Premium",
    "product.sport-gear-store-premium.description":
      "Acquistando questo pacchetto ottieni uno store pronto per vendere, con opzioni per hosting, dominio, SEO, integrazioni social e supporto.",
    "product.outlet-clothing.name": "Outlet-Clothing",
    "product.outlet-clothing.description":
      "Outlet-Clothing — Il tuo e-commerce professionale, pronto a vendere. Questo è un vero esempio di negozio online, veloce, affidabile e progettato per massimizzare le conversioni. Vuoi vedere questo design personalizzato con i tuoi prodotti e il tuo brand? Scegli il pacchetto più adatto alle tue esigenze nella sezione Prezzi. Inizia subito: con un acconto proporzionale al pacchetto scelto blocchiamo l'offerta e mettiamo il tuo store online in pochi giorni.",
    "product.glamour-glow.name": "Glamour Glow — Cosmetici di Lusso",
    "product.glamour-glow.description":
      "Glamour Glow — Il tuo e-commerce professionale, pronto a vendere. Questo è un vero esempio di negozio online, veloce, affidabile e progettato per massimizzare le conversioni. Vuoi vedere questo design personalizzato con i tuoi prodotti e il tuo brand? Scegli il pacchetto più adatto alle tue esigenze nella sezione Prezzi. Inizia subito: con un acconto proporzionale al pacchetto scelto blocchiamo l'offerta e mettiamo il tuo store online in pochi giorni.",
    "product.salumificio-nebrodi.name":
      "Salumificio Nero dei Nebrodi — Salumi Artigianali",
    "product.salumificio-nebrodi.description":
      "Salumificio Nero dei Nebrodi — Il tuo e-commerce professionale, pronto a vendere. Questo è un vero esempio di negozio online, veloce, affidabile e progettato per massimizzare le conversioni. Vuoi vedere questo design personalizzato con i tuoi prodotti e il tuo brand? Scegli il pacchetto più adatto alle tue esigenze nella sezione Prezzi. Inizia subito: con un acconto proporzionale al pacchetto scelto blocchiamo l'offerta e mettiamo il tuo store online in pochi giorni.",
    "product.luxury-boutique.name": "Luxury Boutique",
    "product.luxury-boutique.description":
      "Luxury Boutique — Il tuo e-commerce professionale, pronto a vendere. Questo è un vero esempio di negozio online, veloce, affidabile e progettato per massimizzare le conversioni. Vuoi vedere questo design personalizzato con i tuoi prodotti e il tuo brand? Scegli il pacchetto più adatto alle tue esigenze nella sezione Prezzi. Inizia subito: con un acconto proporzionale al pacchetto scelto blocchiamo l'offerta e mettiamo il tuo store online in pochi giorni.",
    "product.electro-store.name": "Electro Store",
    "product.electro-store.description":
      "Electro Store — Il tuo e-commerce professionale, pronto a vendere. Questo è un vero esempio di negozio online, veloce, affidabile e progettato per massimizzare le conversioni. Vuoi vedere questo design personalizzato con i tuoi prodotti e il tuo brand? Scegli il pacchetto più adatto alle tue esigenze nella sezione Prezzi. Inizia subito: con un acconto proporzionale al pacchetto scelto blocchiamo l'offerta e mettiamo il tuo store online in pochi giorni.",
    "product.hydroflow-depot.name": "Hydroflow Depot",
    "product.hydroflow-depot.description":
      "Hydroflow Depot — Il tuo e-commerce professionale, pronto a vendere. Questo è un vero esempio di negozio online, veloce, affidabile e progettato per massimizzare le conversioni. Vuoi vedere questo design personalizzato con i tuoi prodotti e il tuo brand? Scegli il pacchetto più adatto alle tue esigenze nella sezione Prezzi. Inizia subito: con un acconto proporzionale al pacchetto scelto blocchiamo l'offerta e mettiamo il tuo store online in pochi giorni.",
    "product.oro-verde.name": "Oro Verde",
    "product.oro-verde.description":
      "Oro Verde — Il tuo e-commerce professionale, pronto a vendere. Questo è un vero esempio di negozio online, veloce, affidabile e progettato per massimizzare le conversioni. Vuoi vedere questo design personalizzato con i tuoi prodotti e il tuo brand? Scegli il pacchetto più adatto alle tue esigenze nella sezione Prezzi. Inizia subito: con un acconto proporzionale al pacchetto scelto blocchiamo l'offerta e mettiamo il tuo store online in pochi giorni.",
    "product.pharmacare.name": "PharmaCare",
    "product.pharmacare.description":
      "PharmaCare — Il tuo e-commerce professionale, pronto a vendere. Questo è un vero esempio di negozio online, veloce, affidabile e progettato per massimizzare le conversioni. Vuoi vedere questo design personalizzato con i tuoi prodotti e il tuo brand? Scegli il pacchetto più adatto alle tue esigenze nella sezione Prezzi. Inizia subito: con un acconto proporzionale al pacchetto scelto blocchiamo l'offerta e mettiamo il tuo store online in pochi giorni.",
    "product.nettari-doro.name": "Nettari D'Oro",
    "product.nettari-doro.description":
      "Nettari D'Oro — Il tuo e-commerce professionale, pronto a vendere. Questo è un vero esempio di negozio online, veloce, affidabile e progettato per massimizzare le conversioni. Vuoi vedere questo design personalizzato con i tuoi prodotti e il tuo brand? Scegli il pacchetto più adatto alle tue esigenze nella sezione Prezzi. Inizia subito: con un acconto proporzionale al pacchetto scelto blocchiamo l'offerta e mettiamo il tuo store online in pochi giorni.",
    "product.sapori-etna.name": "Sapori dell'Etna",
    "product.sapori-etna.description":
      "Sapori dell'Etna — Il tuo e-commerce professionale, pronto a vendere. Questo è un vero esempio di negozio online, veloce, affidabile e progettato per massimizzare le conversioni. Vuoi vedere questo design personalizzato con i tuoi prodotti e il tuo brand? Scegli il pacchetto più adatto alle tue esigenze nella sezione Prezzi. Inizia subito: con un acconto proporzionale al pacchetto scelto blocchiamo l'offerta e mettiamo il tuo store online in pochi giorni.",
    "product.sapori-calabria.name": "Sapori di Calabria",
    "product.sapori-calabria.description":
      "Sapori di Calabria — Il tuo e-commerce professionale, pronto a vendere. Questo è un vero esempio di negozio online, veloce, affidabile e progettato per massimizzare le conversioni. Vuoi vedere questo design personalizzato con i tuoi prodotti e il tuo brand? Scegli il pacchetto più adatto alle tue esigenze nella sezione Prezzi. Inizia subito: con un acconto proporzionale al pacchetto scelto blocchiamo l'offerta e mettiamo il tuo store online in pochi giorni.",
    "product.cantina-prestige.name": "Cantina Prestige",
    "product.cantina-prestige.description":
      "Cantina Prestige — Il tuo e-commerce professionale, pronto a vendere. Questo è un vero esempio di negozio online, veloce, affidabile e progettato per massimizzare le conversioni. Vuoi vedere questo design personalizzato con i tuoi prodotti e il tuo brand? Scegli il pacchetto più adatto alle tue esigenze nella sezione Prezzi. Inizia subito: con un acconto proporzionale al pacchetto scelto blocchiamo l'offerta e mettiamo il tuo store online in pochi giorni.",
    "product.forged-performance.name":
      "Forged Performance — Pacchetti e dettagli",
    "product.forged-performance.description":
      "Forged Performance — Il tuo e-commerce professionale, pronto a vendere. Questo è un vero esempio di negozio online, veloce, affidabile e progettato per massimizzare le conversioni. Vuoi vedere questo design personalizzato con i tuoi prodotti e il tuo brand? Scegli il pacchetto più adatto alle tue esigenze nella sezione Prezzi. Inizia subito: con un acconto proporzionale al pacchetto scelto blocchiamo l'offerta e mettiamo il tuo store online in pochi giorni.",
    "product.rosso-corsa.name": "Rosso Corsa Parts",
    "product.rosso-corsa.description":
      "Rosso Corsa Parts — Il tuo e-commerce professionale, pronto a vendere. Questo è un vero esempio di negozio online, veloce, affidabile e progettato per massimizzare le conversioni. Vuoi vedere questo design personalizzato con i tuoi prodotti e il tuo brand? Scegli il pacchetto più adatto alle tue esigenze nella sezione Prezzi. Inizia subito: con un acconto proporzionale al pacchetto scelto blocchiamo l'offerta e mettiamo il tuo store online in pochi giorni.",
    "product.musclefuel.name": "MuscleFuel",
    "product.musclefuel.description":
      "MuscleFuel — Il tuo e-commerce professionale, pronto a vendere. Questo è un vero esempio di negozio online, veloce, affidabile e progettato per massimizzare le conversioni. Vuoi vedere questo design personalizzato con i tuoi prodotti e il tuo brand? Scegli il pacchetto più adatto alle tue esigenze nella sezione Prezzi. Inizia subito: con un acconto proporzionale al pacchetto scelto blocchiamo l'offerta e mettiamo il tuo store online in pochi giorni.",
    "product.tech-haven.name": "Tech Haven",
    "product.tech-haven.description":
      "Tech Haven — Il tuo e-commerce professionale, pronto a vendere. Questo è un vero esempio di negozio online, veloce, affidabile e progettato per massimizzare le conversioni. Vuoi vedere questo design personalizzato con i tuoi prodotti e il tuo brand? Scegli il pacchetto più adatto alle tue esigenze nella sezione Prezzi. Inizia subito: con un acconto proporzionale al pacchetto scelto blocchiamo l'offerta e mettiamo il tuo store online in pochi giorni.",
    "product.happy-paws.name": "Happy Paws",
    "product.happy-paws.description":
      "Happy Paws — Il tuo e-commerce professionale, pronto a vendere. Questo è un vero esempio di negozio online, veloce, affidabile e progettato per massimizzare le conversioni. Vuoi vedere questo design personalizzato con i tuoi prodotti e il tuo brand? Scegli il pacchetto più adatto alle tue esigenze nella sezione Prezzi. Inizia subito: con un acconto proporzionale al pacchetto scelto blocchiamo l'offerta e mettiamo il tuo store online in pochi giorni.",
    "product.urban-threads.name": "Urban Threads",
    "product.urban-threads.description":
      "Urban Threads — Il tuo e-commerce professionale, pronto a vendere. Questo è un vero esempio di negozio online, veloce, affidabile e progettato per massimizzare le conversioni. Vuoi vedere questo design personalizzato con i tuoi prodotti e il tuo brand? Scegli il pacchetto più adatto alle tue esigenze nella sezione Prezzi. Inizia subito: con un acconto proporzionale al pacchetto scelto blocchiamo l'offerta e mettiamo il tuo store online in pochi giorni.",
    "product.levis-outfitters.name": "Levi's Outfitters",
    "product.levis-outfitters.description":
      "Levi's Outfitters — Il tuo e-commerce professionale, pronto a vendere. Questo è un vero esempio di negozio online, veloce, affidabile e progettato per massimizzare le conversioni. Vuoi vedere questo design personalizzato con i tuoi prodotti e il tuo brand? Scegli il pacchetto più adatto alle tue esigenze nella sezione Prezzi. Inizia subito: con un acconto proporzionale al pacchetto scelto blocchiamo l'offerta e mettiamo il tuo store online in pochi giorni.",
    "product.fragrance-boutique.name":
      "Fragrance Boutique — Pacchetti e dettagli",
    "product.fragrance-boutique.description":
      "Fragrance Boutique — Il tuo e-commerce professionale, pronto a vendere. Questo è un vero esempio di negozio online, veloce, affidabile e progettato per massimizzare le conversioni. Vuoi vedere questo design personalizzato con i tuoi prodotti e il tuo brand? Scegli il pacchetto più adatto alle tue esigenze nella sezione Prezzi. Inizia subito: con un acconto proporzionale al pacchetto scelto blocchiamo l'offerta e mettiamo il tuo store online in pochi giorni.",
    "product.explosive-company.name":
      "Explosive Company — Pacchetti e dettagli",
    "product.explosive-company.description":
      "Explosive Company — Il tuo e-commerce professionale, pronto a vendere. Questo è un vero esempio di negozio online, veloce, affidabile e progettato per massimizzare le conversioni. Vuoi vedere questo design personalizzato con i tuoi prodotti e il tuo brand? Scegli il pacchetto più adatto alle tue esigenze nella sezione Prezzi. Inizia subito: con un acconto proporzionale al pacchetto scelto blocchiamo l'offerta e mettiamo il tuo store online in pochi giorni.",

    // template & UI (italian)
    "template.not_found": "Template non trovato.",
    "template.added_to_cart_title": "Aggiunto al carrello",
    "template.added_to_cart_desc": "{variant} aggiunto al carrello.",
    "template.add_to_cart": "Aggiungi al carrello",
    "template.select": "Seleziona {variant}",
    "template.promo_note": "Prezzo promozionale — Offerta limitata",
    "template.savings": "Risparmi {amount}",
    "template.packages_title": "Pacchetti e dettagli",
    "template.packages_intro":
      "Scegli la versione Basic, Pro o Premium: ogni pacchetto include template, strumenti di vendita e servizi opzionali specifici per il tuo settore.",
    "template.next_steps.title": "Prossimi passi:",
    "template.next_steps.1": "Conferma il pacchetto (Basic / Pro / Premium)",
    "template.next_steps.2": "Fornisci dominio e accessi necessari",
    "template.next_steps.3":
      "Definiamo le preferenze di pagamento e spedizione",
    "template.next_steps.4": "Procediamo con il deploy e i test",
    "template.price": "Prezzo",
    "template.toast_conflict_title": "Attenzione",
    "template.toast_conflict_desc":
      "{conflict} è già nel carrello. Rimuovilo prima di aggiungere un'altra versione.",
    "template.case_study_intro":
      "Questo è un vero e-commerce personalizzato per il tuo settore. Veloce, affidabile e pronto a vendere. Scegli il pacchetto più adatto alle tue esigenze nella sezione Prezzi: con un acconto proporzionale blocchiamo l'offerta e mettiamo il tuo store online in pochi giorni.",
    "template.service_details_title": "Tutto Incluso nel Tuo Pacchetto",
    "template.service_details_intro":
      "Servizio completo da inizio a fine. Pensiamo a tutto, tu pensi a vendere.",
    "template.service_detail.deploy_title": "Online in 15 Giorni Lavorativi",
    "template.service_detail.deploy_desc":
      "Il tuo negozio è pronto a vendere. Pensiamo a tutto e lo lanciamo in una settimana.",
    "template.service_detail.setup_title": "Tutto Pronto per Vendere",
    "template.service_detail.setup_desc":
      "Il tuo indirizzo web, pagamenti sicuri con carta, tutto protetto. Pensiamo a tutto noi.",
    "template.service_detail.seo_title": "Trovabile su Google (Setup Base)",
    "template.service_detail.seo_desc":
      "Setup iniziale per far trovare il tuo negozio ai tuoi clienti su Google.",
    "template.service_detail.maintenance_title":
      "Assistenza Dopo il Lancio (Opzionale)",
    "template.service_detail.maintenance_desc":
      "Dopo il lancio del tuo negozio, scegli i nostri piani per mantenerlo veloce e aggiornato. Opzioni mensili disponibili.",
    "template.roi_title": "Perché il servizio è un investimento intelligente",
    "template.roi_vs_dev":
      "Risparmi oltre 7.000€ rispetto a fare tutto da zero, senza rinunciare alla qualità.",
    "template.roi_vs_platforms":
      "Nessuna commissione nascosta che ti ruba guadagni. Tutto quello che guadagni è tuo.",
    "template.roi_guarantee":
      "Il tuo negozio è velocissimo e pronto a crescere con te, anche quando il business esplode.",
    "template.book_deploy_cta":
      "Prenota il Tuo Negozio Online — Inizia in Pochi Giorni",
    "template.contact_cta": "Domande? Contattaci",

    "preview.saas.title": "{brand} — Il tuo Progetto E‑commerce su Misura.",
    "preview.saas.lead":
      "Attenzione: Le nostre piattaforme custom non sono demo pubbliche a basso costo. Questo Case Study è la prova del tuo negozio online: veloce, affidabile e pensato per vendere.",
    "preview.saas.cta_paragraph":
      "Vuoi vedere questo design sul tuo dominio? Scegli la via più rapida: con l'acconto di 199€, iniziamo subito la personalizzazione e il deploy del template sul tuo ambiente. Il tempo è profitto.",
    "preview.saas.footer":
      "Questo template sarà la base del tuo progetto chiavi in mano a 1700€ (sconto 50% limitato nel tempo): lo adattiamo al tuo brand e lo mettiamo online in pochi giorni.",

    "booking.title": "Finalizza la tua prenotazione veloce",
    "booking.subtitle":
      "I tuoi dati saranno usati per avviare il contratto e la consulenza. Ti contatteremo subito.",
    "booking.disclaimer":
      "L'acconto è non rimborsabile e verrà scalato dal saldo finale del pacchetto scelto.",
    "booking.name_label": "Nome e cognome",
    "booking.email_label": "Email aziendale",
    "booking.phone_label": "Numero di Telefono",
    "booking.domain_label": "Dominio desiderato (es. tuomarchio.it)",
    "booking.submit": "Procedi al Checkout (Acconto €199)",
    "booking.submitting": "Reindirizzamento a Stripe…",
    "booking.error_checkout":
      "Impossibile avviare il checkout. Riprova tra qualche minuto.",
    "booking.error_generic":
      "Errore imprevisto durante l'avvio del checkout. Riprova più tardi.",
    "booking.confirm_message":
      "Per bloccare l'avvio del progetto richiediamo un acconto iniziale non rimborsabile che verrà scalato dal saldo finale del pacchetto scelto. Vuoi procedere al pagamento dell'acconto?",

    "partners.title": "Costruito con i migliori partner",
    "partners.subtitle":
      "Progettiamo e mettiamo online il tuo store su uno stack moderno e affidabile con strumenti enterprise-grade per performance, sicurezza e scalabilità.",

    "variant.sport-gear-store-basic.title": "Basic",
    "variant.sport-gear-store-basic.description":
      "Storefront base con catalogo, template prodotto e checkout; ideale per iniziare rapidamente.",
    "variant.sport-gear-store-basic.features":
      "Storefront base con catalogo||Template prodotto e immagini||Checkout semplice||Supporto via documentazione",

    "variant.sport-gear-store.title": "Pro",
    "variant.sport-gear-store.description":
      "Tutto del Basic più manutenzione, aggiornamenti, marketing e integrazioni.",
    "variant.sport-gear-store.features":
      "Tutto del Basic||Manutenzione e aggiornamenti||Ottimizzazioni e supporto prioritario||Integrazione WhatsApp & social||Email di conferma ordine",

    "variant.sport-gear-store-premium.title": "Premium",
    "variant.sport-gear-store-premium.description":
      "Pacchetto completo gestito con hosting, setup dominio e supporto premium.",
    "variant.sport-gear-store-premium.features":
      "Tutto del Pro||Hosting e deploy gestito||Setup dominio, SSL e migrazione||Pagina admin dedicata per prodotti, immagini e prezzi||Campagne iniziali e supporto prioritario",

    "common.close": "Chiudi",
    "common.cancel": "Annulla",
    "common.select_short": "Seleziona",

    "whatsapp.contact": "Contattami su WhatsApp",
    "whatsapp.label": "Consulenza Rapida",
    "whatsapp.message":
      "Ciao BuildStore! Vorrei maggiori informazioni per lanciare il mio nuovo e-commerce.",
    "maintenance.title": "Sito in manutenzione",
    "maintenance.description":
      "Il sito è temporaneamente in manutenzione. Tornerà presto online.",
    "maintenance.enter_code_placeholder":
      "Inserisci codice di accesso per test",
    "maintenance.enter": "Entra",
    "maintenance.exit_btn": "Esci manutenzione",
    "maintenance.error.invalid_code": "Codice errato",
    "maintenance.error.enter_code": "Inserisci il codice di accesso",
    "error.generic": "Errore",
    "error.add_to_cart_failed": "Impossibile aggiungere al carrello",
    "error.download_failed": "Errore durante il download",
    "error.app": "Errore nell'applicazione",

    "admin.title": "Admin — Gestione",
    "admin.login_required":
      "Devi essere loggato per accedere alla pagina admin.",
    "admin.access_denied": "Accesso riservato",
    "admin.access_denied_desc":
      "L'accesso alla pagina amministrativa è riservato ai clienti che hanno acquistato il pacchetto Premium.",
    "admin.checking": "Verifica accesso...",
    "admin.manage": "Admin — Gestione",
    "admin.tab.products": "Prodotti",
    "admin.tab.orders": "Ordini",
    "admin.loading": "Caricamento…",
    "admin.add_product": "Aggiungi prodotto",
    "admin.search": "Cerca",

    "success.title": "Progetto Confermato",
    "success.thanks":
      "La tua prenotazione per il progetto e-commerce è andata a buon fine. Riceverai a breve una email di conferma contenente il riepilogo dell'ordine e le istruzioni per il tuo prossimo appuntamento di kickoff/briefing con il nostro team.",
    "success.download_msg_basic":
      "Ottimo! Hai scelto {variant}. Scarica subito il pacchetto con i template e la guida passo‑passo all'installazione.",
    "success.download_msg_pro":
      "Perfetto! Hai scelto {variant}. Scarica il pacchetto completo insieme alle istruzioni per completare la configurazione e attivare le integrazioni.",
    "success.download_msg_premium":
      "Congratulazioni! Hai scelto {variant}. Scarica subito il pacchetto completo e le istruzioni dettagliate per l'installazione.",
    "success.download_msg_other":
      "Hai acquistato {variant}. Scarica il pacchetto e segui le istruzioni incluse.",
    "success.download_now": "Scarica ora",
    "success.downloading": "Preparazione download...",
    "success.no_product_selected":
      "Nessun pacchetto selezionato per il download",
    "success.back_to_shop": "Torna allo shop",
    "success.go_admin": "Vai a Admin",

    "cancel.title": "Pagamento annullato",
    "cancel.desc": "Hai annullato il checkout. Puoi riprovare quando vuoi.",
    "cancel.back_to_shop": "Torna allo shop",

    "auth.error.user_not_found": "Utente non trovato. Registrati prima.",
    "auth.error.wrong_password": "Password errata",
    "auth.error.email_required": "Email e password richieste",
    "auth.error.account_exists": "Account già esistente per questa email",

    "value.hero.title":
      "Possiedi la tua piattaforma, raddoppia la velocità, tieni il 100% dei tuoi profitti.",
    "value.hero.subtitle":
      "Le piattaforme SaaS ti fanno pagare per crescere. BuildStore.it è l'investimento definitivo in performance, velocità e controllo totale.",

    "value.comparison.criteria": "Criterio di Scelta",
    "value.comparison.shopify": "🛍️ Piattaforme in Affitto (Shopify, Wix)",
    "value.comparison.webador": "🔧 Website Builder (Webador)",
    "value.comparison.buildstore":
      "🚀 BuildStore.it (Stack Moderno & Controllo Totale)",

    "value.comparison.rows.cost.label": "Modello di Costo",
    "value.comparison.rows.cost.shopify":
      "Canone mensile + Commissioni su ogni vendita.",
    "value.comparison.rows.cost.webador": "Gratuito / €5,50 - €20 al mese",
    "value.comparison.rows.cost.buildstore":
      "Prezzi Trasparenti (da €299): Nessun canone mensile, zero commissioni sulle vendite. Paghi il valore, non l'affitto.",

    "value.comparison.rows.ownership.label": "Proprietà del Codice",
    "value.comparison.rows.ownership.shopify":
      "Il codice appartiene al provider (Sei in affitto).",
    "value.comparison.rows.ownership.webador":
      "Il codice appartiene al provider (Sei in affitto).",
    "value.comparison.rows.ownership.buildstore":
      "Proprietà Totale: L'intero codice sorgente è tuo. Lo sposti dove vuoi, quando vuoi.",

    "value.comparison.rows.technology.label": "Tecnologia e Velocità",
    "value.comparison.rows.technology.shopify":
      "Codice legacy, velocità media. Dipendente dal server.",
    "value.comparison.rows.technology.webador":
      "Hosting tradizionale, velocità limitata.",
    "value.comparison.rows.technology.buildstore":
      "Architettura Cloud Avanzata a Risposta Istantanea. Tecnologia proprietaria ottimizzata per eliminare ogni tempo di caricamento.",

    "value.comparison.rows.management.label": "Manutenzione Tecnica",
    "value.comparison.rows.management.shopify":
      "Ti occupi di app, aggiornamenti e bug.",
    "value.comparison.rows.management.webador":
      "Automatizzata dalla piattaforma.",
    "value.comparison.rows.management.buildstore":
      "Consegnato Pronto: Piattaforma costruita e ottimizzata da esperti. Tu vendi, noi costruiamo.",

    "value.comparison.rows.seo.label": "Ottimizzazione SEO",
    "value.comparison.rows.seo.shopify": "Strumenti SEO inclusi ma limitati.",
    "value.comparison.rows.seo.webador": "Ottimizzazione SEO base.",
    "value.comparison.rows.seo.buildstore":
      "Ottimizzato per Core Web Vitals: La velocità è il primo fattore di ranking di Google. La garantiamo.",

    "value.comparison.rows.scalability.label": "Scalabilità",
    "value.comparison.rows.scalability.shopify":
      "Richiede upgrade costosi per gestire grandi volumi di traffico.",
    "value.comparison.rows.scalability.webador": "Limitata a traffico medio.",
    "value.comparison.rows.scalability.buildstore":
      "Nata per Scalare: Architettura che gestisce milioni di visitatori senza pagare per server inattivi.",

    "value.comparison.rows.customization.label": "Personalizzazione",
    "value.comparison.rows.customization.shopify":
      "Limitata ai temi e all'ecosistema di App.",
    "value.comparison.rows.customization.webador": "Limitata ai template.",
    "value.comparison.rows.customization.buildstore":
      "Illimitata: Design e funzionalità 100% personalizzabili (grazie allo Stack moderno).",

    "value.conclusion.title": "Smetti di Affittare. Inizia a Possedere.",
    "value.conclusion.text":
      "Perché spendere centinaia di euro in canoni e commissioni a Shopify, quando puoi investire quei soldi una volta in una piattaforma proprietaria, infinitamente più veloce e scalabile? BuildStore.it ti offre l'eccellenza tecnologica che i builder non possono darti. Il tuo successo è il tuo, non il nostro.",

    "value.faq.title": "Domande Frequenti",

    "value.faq.questions.0.q":
      "Quali sono i costi ricorrenti dopo il pagamento dell'acconto?",
    "value.faq.questions.0.a":
      "Dopo la configurazione iniziale, non pagherai alcun canone mensile a noi. Gli unici costi ricorrenti riguardano il mantenimento dell'infrastruttura globale e del database, che pagherai direttamente ai provider infrastrutturali in base all'uso effettivo. Grazie alla nostra Architettura Cloud-Native, questi costi sono minimi (spesso vicini allo zero per volumi medio-piccoli) e scalano solo insieme al tuo successo. Risparmi migliaia di euro all'anno eliminando i canoni fissi e le commissioni delle piattaforme tradizionali.",

    "value.faq.questions.1.q": "La piattaforma e il codice sono miei?",
    "value.faq.questions.1.a":
      "Assolutamente sì. A differenza di Wix o Shopify, tu sei il proprietario al 100% del codice sorgente e di tutti i dati. Hai il controllo completo e l'accesso illimitato al tuo asset digitale.",

    "value.faq.questions.2.q":
      "Cos'è esattamente la tecnologia BuildStore e perché è più veloce dei sistemi comuni?",
    "value.faq.questions.2.a":
      "Utilizziamo un'infrastruttura Cloud-Native di nuova generazione che separa il design dalla logica di gestione. A differenza dei sistemi tradizionali (Shopify/Wix) che si affidano a pesanti server centrali, la nostra tecnologia distribuisce il tuo sito su una rete globale intelligente. Questo significa che il tuo negozio non deve 'aspettare' un server lontano: è già presente ovunque si trovi il tuo cliente, garantendo una velocità che i sistemi standard non possono fisicamente raggiungere.",

    "value.faq.questions.3.q":
      "BuildStore.it può gestire picchi di traffico elevati?",
    "value.faq.questions.3.a":
      "Sì. La nostra architettura è costruita per scalare orizzontalmente in modo automatico. La tua piattaforma può gestire senza problemi milioni di richieste e picchi di traffico improvvisi, senza che tu debba preoccuparti di crash o upgrade di server. L'abbiamo ingegnerizzata per rimanere veloce anche sotto carico estremo.",

    "value.faq.questions.4.q":
      "Ho bisogno di conoscenze tecniche per gestire il negozio ogni giorno?",
    "value.faq.questions.4.a":
      "Assolutamente no. Ti consegniamo il tuo negozio con una dashboard intuitiva (CMS) dove puoi gestire prodotti, ordini e contenuti facilmente come su qualsiasi altra piattaforma. La differenza? Il tuo sito è 10 volte più veloce.",

    "value.faq.questions.5.q":
      "E se volessi aggiungere una funzionalità complessa in futuro?",
    "value.faq.questions.5.a":
      "Poiché la piattaforma è interamente basata sul codice e non è bloccata in un'app store (come Shopify), la personalizzazione è illimitata. Possiamo costruire qualsiasi funzionalità complessa senza incontrare i limiti di un builder standard. La tua piattaforma è flessibile quanto la tua ambizione.",

    "value.faq.questions.6.q": "Come funziona il processo di deployment?",
    "value.faq.questions.6.a":
      "Una volta approvato il design, attiviamo la tua istanza proprietaria sulla nostra infrastruttura globale. Il passaggio 'Live' è istantaneo: coordiniamo il puntamento del dominio e la propagazione dei protocolli di sicurezza, consegnandoti le chiavi di una piattaforma già configurata, sicura e pronta a vendere.",

    "value.faq.questions.7.q": "Che supporto ricevo dopo il lancio?",
    "value.faq.questions.7.a":
      "Dopo il lancio, puoi attivare i nostri piani di hosting gestito e manutenzione a partire da un canone mensile. Forniamo anche supporto tecnico per aiutarti a ottimizzare e far crescere il tuo business.",

    "value.cta.title": "Pronto a Possedere la Tua Piattaforma?",
    "value.cta.text":
      "Smetti di pagare abbonamenti mensili. Investi in BuildStore.it e possiedi il tuo asset digitale per sempre.",
    "value.cta.button": "Richiedi una Consulenza",

    // ⭐ NUOVE TRADUZIONI PER LA PAGINA /valore
    "value.meta.description":
      "BuildStore.it: Oltre Shopify e Wix | E-commerce Proprietario",
    "value.meta.title":
      "BuildStore.it - Valore: Oltre Shopify e Wix | E-commerce Proprietario",

    // Blog Access Gate - Italian
    "blog.access.title": "Accesso Blog Premium",
    "blog.access.subtitle":
      "Sblocca insight premium, strategie e tattica provate che valgono migliaia di euro di consulenza.",
    "blog.access.benefit1.title": "Conoscenza Expert",
    "blog.access.benefit1.desc":
      "Best practice industriali e strategie provate",
    "blog.access.benefit2.title": "Accesso Vita",
    "blog.access.benefit2.desc": "Un pagamento per accesso permanente",
    "blog.access.benefit3.title": "Aggiornamenti Regolari",
    "blog.access.benefit3.desc":
      "Nuovo contenuto e insight aggiunti mensilmente",
    "blog.access.payment_note": "Un pagamento per accesso vita",
    "blog.access.form.fullname": "Nome Completo",
    "blog.access.form.fullname_placeholder": "Il tuo nome",
    "blog.access.form.email": "Indirizzo Email",
    "blog.access.form.email_placeholder": "tuoemail@email.com",
    "blog.access.form.phone": "Numero di Telefono",
    "blog.access.form.phone_placeholder": "+39 ...",
    "blog.access.form.submit": "Sblocca Accesso Premium Blog",
    "blog.access.form.processing": "Elaborazione...",
    "blog.access.form.security":
      "Pagamento sicuro elaborato. I tuoi dati sono protetti e non verranno mai condivisi.",
    "blog.access.faq.title": "Domande?",
    "blog.access.faq.q1": "Che cosa ricevo?",
    "blog.access.faq.a1":
      "Accesso completo a tutti gli articoli del blog premium, case study e guide di ottimizzazione.",
    "blog.access.faq.q2": "È un abbonamento?",
    "blog.access.faq.a2":
      "No. Un pagamento ti dà accesso vita. Nessun canone ricorrente.",
    "blog.access.faq.q3": "Quali metodi di pagamento accettate?",
    "blog.access.faq.a3":
      "Accettiamo tutte le principali carte di credito tramite elaborazione pagamenti sicura.",
    "blog.access.success":
      "Benvenuto nell'accesso blog premium! Hai accesso vita a tutti i nostri insight e strategie premium.",
    "blog.access.success_cta": "Vai al Blog",

    "case_studies.title": "I Nostri Casi Studio",
    "case_studies.description":
      "Esplora esempi reali e funzionanti di piattaforme e-commerce che abbiamo costruito per aziende di diversi settori. Scopri la potenza di BuildStore in azione.",
    "case_studies.cta_title": "Pronto a Costruire la Tua Piattaforma?",
    "case_studies.cta_description":
      "Creiamo qualcosa di straordinario insieme. Contattaci per discutere la tua visione di e-commerce.",
    "case_studies.cta_button": "Inizia il Tuo Progetto",

    "case_study.challenge": "La Sfida",
    "case_study.solution": "La Soluzione",
    "case_study.results": "I Risultati",
    "case_study.modern_stack": "Tecnologia Moderna e Enterprise-Grade",
    "case_study.modern_stack_description":
      "Costruito su architettura serverless all'avanguardia con CDN globale, database scalabili e infrastruttura basata sulla sicurezza. Non una piattaforma template—una soluzione completamente personalizzata e proprietaria, progettata per performance e crescita.",
    "case_study.modern_stack_speed": "Velocità Fulminea",
    "case_study.modern_stack_speed_desc":
      "Ottimizzato per Core Web Vitals con tempi di caricamento sotto i 2 secondi. Ogni millisecondo conta per le conversioni.",
    "case_study.modern_stack_secure": "Sicurezza Enterprise",
    "case_study.modern_stack_secure_desc":
      "Crittografia SSL/TLS, backup automatici e monitoraggio 24/7. I tuoi dati e quelli dei clienti sono protetti.",
    "case_study.modern_stack_scalable": "Infinitamente Scalabile",
    "case_study.modern_stack_scalable_desc":
      "Gestisci picchi di traffico automaticamente. Un'architettura che cresce con il tuo business senza upgrade costosi.",
    "case_study.modern_stack_owned": "100% Tuo",
    "case_study.modern_stack_owned_desc":
      "Proprietà completa del codice. Nessun vendor lock-in, nessuna commissione nascosta. Il tuo asset digitale, per sempre.",
    "case_study.visit_title": "Visita la Piattaforma Live",
    "case_study.visit_button": "Visita il Sito",
    "case_study.click_to_visit": "Clicca per visitare il sito",
    "case_study.explore_more": "Esplora Più Casi Studio",
    "case_study.back_to_all": "Vedi Tutti i Casi",
    "case_study.back_to_cases": "Torna ai Casi Studio",
    "case_study.breadcrumb_cases": "Casi Studio",
    "case_study.not_found_title": "Caso Studio Non Trovato",
    "case_study.not_found_description":
      "Scusa, non abbiamo trovato il caso studio che stai cercando.",

    // Sicilian Bio Food Case Study Content - Italian
    "case_study.sicilianbiofood.description":
      "Una piattaforma e-commerce premium per autentici prodotti alimentari biologici e artigianali siciliani. Progettata per mostrare la ricchezza delle ricette tradizionali con un'esperienza digitale moderna.",
    "case_study.sicilianbiofood.challenge":
      "Il cliente aveva bisogno di una presenza online robusta per raggiungere clienti in Italia e in Europa, mantenendo l'autentica narrazione del suo patrimonio alimentare biologico. Richiedeva una piattaforma che potesse gestire variazioni di prodotto stagionali e evidenziare la qualità delle loro offerte.",
    "case_study.sicilianbiofood.solution":
      "Abbiamo sviluppato una soluzione e-commerce ad alte prestazioni costruita su architettura serverless moderna. La piattaforma offre gallerie di prodotti straordinarie con descrizioni ricche, elaborazione pagamenti integrata, gestione dell'inventario e un'esperienza di checkout senza soluzione di continuità ottimizzata per il retail alimentare. Il design enfatizza l'impegno del brand verso la qualità e la tradizione.",
    "case_study.sicilianbiofood.results":
      "La piattaforma è pronta per generare crescita. Con il lancio ufficiale, Sicilian Bio Food potrà raggiungere clienti in tutta Italia e Europa, espandendo la base clienti da locale a internazionale e massimizzando le vendite online.",

    "checkout.deposit_message":
      "Acconto per la prenotazione del progetto. L'importo verrà scalato dal saldo finale del pacchetto scelto.",

    "services.seo.name": "Ottimizzazione SEO",
    "services.seo.price": "A partire da €400",
    "services.seo.description":
      "Setup SEO completo con audit, ottimizzazione e configurazione Google Analytics.",

    "services.migration.name": "Migrazione Piattaforma / Sviluppo Custom",
    "services.migration.price": "A partire da €400",
    "services.migration.description":
      "Migrazione da altre piattaforme o sviluppo di funzionalità personalizzate per le tue esigenze specifiche.",

    "services.multilingual.name": "Espansione Mercati (Multilingua)",
    "services.multilingual.price": "A partire da €450 per lingua",
    "services.multilingual.description":
      "Internazionalizza il tuo store con supporto multilingue e ottimizzazione SEO.",

    "checkout.ecommerce_deposit_message":
      "Acconto prenotazione progetto e-commerce. Scalato dal totale del pacchetto scelto.",
    "checkout.service_full_payment_message":
      "Pagamento completo per il servizio selezionato. Soluzione professionale pronta in 15 giorni lavorativi.",
    "checkout.showcase_full_payment_message":
      "Pagamento saldo per sito vetrina professionale. Pronto in 15 giorni lavorativi.",
  },
};

const LocaleContext = createContext({
  lang: "en" as Lang,
  setLang: (l: Lang) => {},
  t: (key: string, vars?: Record<string, any>) => key,
});

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const [lang, setLangState] = useState<Lang>(() => {
    if (typeof window === "undefined") return "en";
    const urlLang = location.pathname.startsWith("/en") ? "en" : "it";
    return (localStorage.getItem("fusion.lang") as Lang) || urlLang;
  });

  useEffect(() => {
    const urlLang = location.pathname.startsWith("/en") ? "en" : "it";
    setLangState(urlLang);
  }, [location.pathname]);

  useEffect(() => {
    try {
      localStorage.setItem("fusion.lang", lang);
    } catch {}
  }, [lang]);

  const setLang = (l: Lang) => setLangState(l);

  const t = (key: string, vars?: Record<string, any>) => {
    let template = translations[lang][key] ?? translations["en"][key] ?? key;

    if (!vars) return template;

    // Handle simple plural syntax: {count, plural, one {one form} other {other form}}
    template = template.replace(
      /\{(\w+),\s*plural,\s*one\s*\{([^}]*)\}\s*other\s*\{([^}]*)\}\s*\}/g,
      (_m, varName, oneForm, otherForm) => {
        const v = Number(vars[varName]);
        return isNaN(v) ? otherForm : v === 1 ? oneForm : otherForm;
      },
    );

    // Replace variables {name}
    template = template.replace(/\{(\w+)\}/g, (_m, varName) => {
      const val = vars[varName];
      return val === undefined || val === null ? "" : String(val);
    });

    return template;
  };

  return (
    <LocaleContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LocaleContext.Provider>
  );
};

export function useLocale() {
  return useContext(LocaleContext);
}
